// Record in Chat: record control, recording strip, capture view, live preview and list rows
// (C-14).
//
// Namespace `meetings-record`; owner WP-4 (BUILD 1). Only the owner adds keys or
// translations. Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  // Meetings page
  pageEyebrow: '{company} workspace',
  pageTitle: 'Meetings',
  pageDescription:
    'Record a meeting here, or import a recording or transcript. Poneva keeps the original and prepares cited notes using your company context.',
  loadingMeetings: 'Loading meetings…',
  loadFailed: 'We could not load meetings.',
  emptyTitle: 'Every meeting, ready to use.',
  emptyDescription:
    'Record a meeting, or import a recording or transcript. The original stays available while Poneva prepares the transcript and cited follow-ups.',
  importMeeting: 'Import meeting',
  importFirst: 'Import your first meeting',
  importUnavailable:
    'Importing audio is not available on this server. You can still import transcripts.',
  rowRecording: 'Recording… {time}',
  rowSaving: 'Saving…',
  // Import dialog
  importDialogTitle: 'Import a meeting',
  importRetainedNote:
    'The original recording is retained. Audio is processed into a smaller speech copy without replacing the original.',
  importSaved: 'Meeting saved. Transcription is running in the background.',
  importFailed: 'We could not import this meeting.',
  uploadProgress: 'Upload progress',
  uploadingPercent: 'Uploading {percent}%',
  // Shared dialog parts
  titleLabel: 'Title',
  membersLabel: 'People from {company}',
  removeMember: 'Remove {name}',
  searchMembers: 'Search company members',
  you: '(you)',
  noMatchingMembers: 'No matching company members.',
  guestsLabel: 'Other attendees',
  optional: '(optional)',
  guestsPlaceholder: 'One name or email per line',
  cancel: 'Cancel',
  close: 'Close',
  dismiss: 'Dismiss',
  retryStop: 'Try again',
  // Record control
  record: 'Record',
  starting: 'Starting…',
  recordDialogTitle: 'Record a meeting',
  recordDialogNote:
    'Audio is saved to Chat while you record. The transcript and notes are made from the recording after you stop.',
  sourceLabel: 'What to record',
  sourceRoom: 'This room',
  sourceRoomHint: 'The microphone of this device.',
  sourceTab: 'A meeting tab',
  sourceTabHint: 'The sound of a call in another browser tab.',
  sourceBoth: 'Both',
  sourceBothHint: 'The call tab and the microphone of this device.',
  tabPickerNote:
    'Choose the meeting’s browser tab and turn on “Share tab audio”. The browser calls this screen sharing, but Poneva records only audio and immediately stops the video. Choose Both to include your microphone.',
  consentLine: 'Everyone in the meeting knows it is being recorded.',
  startRecording: 'Start recording',
  defaultTitle: 'Meeting {date}',
  // While recording
  stop: 'Stop',
  stopRecordingLabel: 'Stop recording ({time})',
  inputLevel: 'Microphone level',
  elapsed: 'Recording {time}',
  uploadBacklog: '{seconds} s of audio waiting to upload.',
  saving: 'Saving the recording… {seconds} s of audio left to upload.',
  savingOffline: 'Offline. The recording is kept on this device and uploads when you reconnect.',
  sealing: 'Saving the recording',
  recordingNow: 'Recording in progress',
  recordingElsewhere: 'This meeting is being recorded on another device.',
  openRecording: 'Open',
  discardRecording: 'Discard the recording',
  resume: 'Resume recording',
  stopHere: 'Stop and save',
  recoveryTitle: 'A recording was interrupted',
  recoveryBody:
    'The audio recorded before the interruption is saved. Resume to keep recording, or stop and save it.',
  tabEnded: 'The meeting tab stopped sharing. Recording continues from the microphone.',
  micEnded: 'The microphone stopped. Recording continues from the meeting tab.',
  micLost: 'The microphone stopped, so the recording paused.',
  limitWarning: 'The recording stops in {minutes} min.',
  limitReached: 'The recording reached its maximum length and was stopped.',
  // Live preview
  previewCaption: 'Live preview. The final transcript comes from the recording.',
  previewPaused: 'Live preview paused.',
  previewLag: 'Live text is {seconds} s behind.',
  previewNotShown: '{seconds} s of audio was not shown live. It is still recorded.',
  previewUnavailable: 'Live preview is unavailable. Recording continues.',
  // Errors
  micDenied: 'Allow the microphone and try again.',
  noMic: 'No microphone was found.',
  micBusy: 'The microphone is being used by another app.',
  insecureOrigin: 'Recording needs a secure (https) connection.',
  shareCancelled: 'Sharing the tab was cancelled.',
  shareNoAudio:
    'Choose the meeting’s browser tab with “Share tab audio” enabled. A window or entire screen cannot be used for meeting-tab audio.',
  tabUnsupported:
    'This browser cannot capture meeting-tab audio. Use the microphone here, open the meeting and Poneva in desktop Chrome or Edge, or import a recording.',
  storageLow: 'This device is low on storage. Free some space before recording.',
  captureClosed: 'This recording has already been stopped.',
  captureActiveElsewhere: 'You are already recording another meeting.',
  recordUnavailable: 'Recording is not available right now.',
  stopFailed:
    'The recording could not be stopped. Check your connection or sign in again, then try again.',
  signOutRecordingTitle: 'A meeting recording is still in progress',
  signOutRecordingBody:
    'Signing out stops it. Audio that is not uploaded yet stays on this device and finishes uploading the next time you sign in here.',
  signOutAnyway: 'Sign out',
  paymentRequired:
    "Your company's usage credit is used up. An administrator can add a card, then try again.",
};

export type MeetingsRecordCopyKey = keyof typeof en;

const meetingsRecordCopy: NamespaceCatalog<MeetingsRecordCopyKey> = {
  bg: {
    pageEyebrow: 'Работно пространство на {company}',
    pageTitle: 'Срещи',
    pageDescription:
      'Запишете среща тук или импортирайте запис или транскрипция. Poneva пази оригинала и подготвя бележки с източници въз основа на контекста на вашата компания.',
    loadingMeetings: 'Срещите се зареждат…',
    loadFailed: 'Срещите не можаха да бъдат заредени.',
    emptyTitle: 'Всяка среща, готова за използване.',
    emptyDescription:
      'Запишете среща или импортирайте запис или транскрипция. Оригиналът остава достъпен, докато Poneva подготвя транскрипцията и следващите стъпки с източници.',
    importMeeting: 'Импортиране на среща',
    importFirst: 'Импортирайте първата си среща',
    importUnavailable:
      'Импортирането на аудио не е налично на този сървър. Все още можете да импортирате транскрипции.',
    rowRecording: 'Записва се… {time}',
    rowSaving: 'Запазва се…',
    importDialogTitle: 'Импортиране на среща',
    importRetainedNote:
      'Оригиналният запис се запазва. Аудиото се обработва в по-малко копие на речта, без да замества оригинала.',
    importSaved: 'Срещата е запазена. Транскрибирането продължава във фонов режим.',
    importFailed: 'Тази среща не можа да бъде импортирана.',
    uploadProgress: 'Напредък на качването',
    uploadingPercent: 'Качване {percent}%',
    titleLabel: 'Заглавие',
    membersLabel: 'Хора от {company}',
    removeMember: 'Премахване на {name}',
    searchMembers: 'Търсене на членове на компанията',
    you: '(вие)',
    noMatchingMembers: 'Няма съвпадащи членове.',
    guestsLabel: 'Други участници',
    optional: '(по избор)',
    guestsPlaceholder: 'По едно име или имейл на ред',
    cancel: 'Отказ',
    close: 'Затваряне',
    dismiss: 'Скриване',
    retryStop: 'Опитайте пак',
    record: 'Запис',
    starting: 'Стартиране…',
    recordDialogTitle: 'Записване на среща',
    recordDialogNote:
      'Аудиото се запазва в Chat, докато записвате. Транскрипцията и бележките се правят от записа, след като спрете.',
    sourceLabel: 'Какво да се запише',
    sourceRoom: 'Тази стая',
    sourceRoomHint: 'Микрофонът на това устройство.',
    sourceTab: 'Раздел със среща',
    sourceTabHint: 'Звукът от разговор в друг раздел на браузъра.',
    sourceBoth: 'И двете',
    sourceBothHint: 'Разделът с разговора и микрофонът на това устройство.',
    tabPickerNote:
      'Изберете раздела на срещата и включете „Споделяне на аудиото на раздела“. Браузърът нарича това споделяне на екрана, но Poneva записва само аудио и веднага спира видеото. Изберете „И двете“, за да включите микрофона.',
    consentLine: 'Всички участници в срещата знаят, че тя се записва.',
    startRecording: 'Започване на записа',
    defaultTitle: 'Среща {date}',
    stop: 'Спиране',
    stopRecordingLabel: 'Спиране на записа ({time})',
    inputLevel: 'Ниво на микрофона',
    elapsed: 'Записва се {time}',
    uploadBacklog: '{seconds} с аудио чакат качване.',
    saving: 'Записът се запазва… Остават за качване {seconds} с аудио.',
    savingOffline:
      'Няма връзка. Записът остава на това устройство и ще се качи, когато се свържете отново.',
    sealing: 'Записът се запазва',
    recordingNow: 'Записът е в ход',
    recordingElsewhere: 'Тази среща се записва на друго устройство.',
    openRecording: 'Отваряне',
    discardRecording: 'Отхвърляне на записа',
    resume: 'Продължаване на записа',
    stopHere: 'Спиране и запазване',
    recoveryTitle: 'Запис беше прекъснат',
    recoveryBody:
      'Аудиото, записано преди прекъсването, е запазено. Продължете записа или го спрете и запазете.',
    tabEnded: 'Разделът със срещата вече не се споделя. Записът продължава от микрофона.',
    micEnded: 'Микрофонът спря. Записът продължава от раздела със срещата.',
    micLost: 'Микрофонът спря, затова записът е на пауза.',
    limitWarning: 'Записът спира след {minutes} мин.',
    limitReached: 'Записът достигна максималната си дължина и беше спрян.',
    previewCaption: 'Преглед на живо. Окончателната транскрипция се прави от записа.',
    previewPaused: 'Прегледът на живо е на пауза.',
    previewLag: 'Текстът на живо изостава с {seconds} с.',
    previewNotShown: '{seconds} с аудио не бяха показани на живо. Те все пак са записани.',
    previewUnavailable: 'Прегледът на живо не е наличен. Записът продължава.',
    micDenied: 'Разрешете микрофона и опитайте отново.',
    noMic: 'Не е намерен микрофон.',
    micBusy: 'Микрофонът се използва от друго приложение.',
    insecureOrigin: 'За записа е нужна защитена (https) връзка.',
    shareCancelled: 'Споделянето на раздела беше отменено.',
    shareNoAudio:
      'Изберете раздела на срещата с включено „Споделяне на аудиото на раздела“. Прозорец или цял екран не може да се използва за аудио от раздел.',
    tabUnsupported:
      'Този браузър не може да записва аудио от раздел на среща. Използвайте микрофона тук, отворете срещата и Poneva в Chrome или Edge на компютър или импортирайте запис.',
    storageLow: 'На устройството почти няма свободно място. Освободете място преди записа.',
    captureClosed: 'Този запис вече е спрян.',
    captureActiveElsewhere: 'Вече записвате друга среща.',
    recordUnavailable: 'Записването не е налично в момента.',
    stopFailed:
      'Записът не можа да бъде спрян. Проверете връзката или влезте отново, после опитайте пак.',
    signOutRecordingTitle: 'Запис на среща все още е в ход',
    signOutRecordingBody:
      'Излизането го спира. Звукът, който още не е качен, остава на това устройство и се качва при следващото ви влизане тук.',
    signOutAnyway: 'Изход',
    paymentRequired:
      'Кредитът за използване на фирмата ви е изчерпан. Администратор може да добави карта, след което опитайте отново.',
  },
  hr: {
    pageEyebrow: 'Radni prostor {company}',
    pageTitle: 'Sastanci',
    pageDescription:
      'Snimite sastanak ovdje ili uvezite snimku ili transkript. Poneva čuva izvornik i priprema bilješke s izvorima na temelju konteksta vaše tvrtke.',
    loadingMeetings: 'Učitavanje sastanaka…',
    loadFailed: 'Sastanke nije bilo moguće učitati.',
    emptyTitle: 'Svaki sastanak, spreman za upotrebu.',
    emptyDescription:
      'Snimite sastanak ili uvezite snimku ili transkript. Izvornik ostaje dostupan dok Poneva priprema transkript i daljnje korake s izvorima.',
    importMeeting: 'Uvezi sastanak',
    importFirst: 'Uvezite svoj prvi sastanak',
    importUnavailable:
      'Uvoz zvuka nije dostupan na ovom poslužitelju. I dalje možete uvoziti transkripte.',
    rowRecording: 'Snimanje… {time}',
    rowSaving: 'Spremanje…',
    importDialogTitle: 'Uvoz sastanka',
    importRetainedNote:
      'Izvorna snimka se čuva. Zvuk se obrađuje u manju govornu kopiju bez zamjene izvornika.',
    importSaved: 'Sastanak je spremljen. Transkripcija se odvija u pozadini.',
    importFailed: 'Ovaj sastanak nije bilo moguće uvesti.',
    uploadProgress: 'Napredak prijenosa',
    uploadingPercent: 'Prijenos {percent} %',
    titleLabel: 'Naslov',
    membersLabel: 'Osobe iz {company}',
    removeMember: 'Ukloni: {name}',
    searchMembers: 'Pretraži članove tvrtke',
    you: '(vi)',
    noMatchingMembers: 'Nema odgovarajućih članova.',
    guestsLabel: 'Ostali sudionici',
    optional: '(neobavezno)',
    guestsPlaceholder: 'Jedno ime ili e-adresa po retku',
    cancel: 'Odustani',
    close: 'Zatvori',
    dismiss: 'Odbaci',
    retryStop: 'Pokušaj ponovno',
    record: 'Snimi',
    starting: 'Pokretanje…',
    recordDialogTitle: 'Snimi sastanak',
    recordDialogNote:
      'Zvuk se tijekom snimanja sprema u Chat. Transkript i bilješke izrađuju se iz snimke nakon zaustavljanja.',
    sourceLabel: 'Što snimiti',
    sourceRoom: 'Ova prostorija',
    sourceRoomHint: 'Mikrofon ovog uređaja.',
    sourceTab: 'Kartica sastanka',
    sourceTabHint: 'Zvuk poziva u drugoj kartici preglednika.',
    sourceBoth: 'Oboje',
    sourceBothHint: 'Kartica poziva i mikrofon ovog uređaja.',
    tabPickerNote:
      'Odaberite karticu sastanka i uključite „Dijeli zvuk kartice”. Preglednik to naziva dijeljenjem zaslona, ali Poneva snima samo zvuk i odmah zaustavlja video. Odaberite „Oboje” za uključivanje mikrofona.',
    consentLine: 'Svi na sastanku znaju da se snima.',
    startRecording: 'Započni snimanje',
    defaultTitle: 'Sastanak {date}',
    stop: 'Zaustavi',
    stopRecordingLabel: 'Zaustavi snimanje ({time})',
    inputLevel: 'Razina mikrofona',
    elapsed: 'Snimanje {time}',
    uploadBacklog: '{seconds} s zvuka čeka na prijenos.',
    saving: 'Spremanje snimke… Preostalo je prenijeti {seconds} s zvuka.',
    savingOffline:
      'Izvan mreže. Snimka ostaje na ovom uređaju i prenosi se kad se ponovno povežete.',
    sealing: 'Spremanje snimke',
    recordingNow: 'Snimanje je u tijeku',
    recordingElsewhere: 'Ovaj se sastanak snima na drugom uređaju.',
    openRecording: 'Otvori',
    discardRecording: 'Odbaci snimku',
    resume: 'Nastavi snimanje',
    stopHere: 'Zaustavi i spremi',
    recoveryTitle: 'Snimanje je prekinuto',
    recoveryBody:
      'Zvuk snimljen prije prekida je spremljen. Nastavite snimanje ili ga zaustavite i spremite.',
    tabEnded: 'Kartica sastanka više se ne dijeli. Snimanje se nastavlja s mikrofona.',
    micEnded: 'Mikrofon je prestao raditi. Snimanje se nastavlja s kartice sastanka.',
    micLost: 'Mikrofon je prestao raditi pa je snimanje pauzirano.',
    limitWarning: 'Snimanje se zaustavlja za {minutes} min.',
    limitReached: 'Snimka je dosegla najveću duljinu i zaustavljena je.',
    previewCaption: 'Pregled uživo. Konačni transkript izrađuje se iz snimke.',
    previewPaused: 'Pregled uživo je pauziran.',
    previewLag: 'Tekst uživo kasni {seconds} s.',
    previewNotShown: '{seconds} s zvuka nije prikazano uživo. I dalje je snimljeno.',
    previewUnavailable: 'Pregled uživo nije dostupan. Snimanje se nastavlja.',
    micDenied: 'Dopustite pristup mikrofonu i pokušajte ponovno.',
    noMic: 'Mikrofon nije pronađen.',
    micBusy: 'Mikrofon koristi druga aplikacija.',
    insecureOrigin: 'Za snimanje je potrebna sigurna (https) veza.',
    shareCancelled: 'Dijeljenje kartice je otkazano.',
    shareNoAudio:
      'Odaberite karticu sastanka uz uključeno „Dijeli zvuk kartice”. Prozor ili cijeli zaslon ne mogu se koristiti za zvuk kartice sastanka.',
    tabUnsupported:
      'Ovaj preglednik ne može snimati zvuk kartice sastanka. Ovdje koristite mikrofon, otvorite sastanak i Ponevu u Chromeu ili Edgeu na računalu ili uvezite snimku.',
    storageLow: 'Na ovom uređaju ponestaje prostora. Oslobodite prostor prije snimanja.',
    captureClosed: 'Ovo snimanje je već zaustavljeno.',
    captureActiveElsewhere: 'Već snimate drugi sastanak.',
    recordUnavailable: 'Snimanje trenutačno nije dostupno.',
    stopFailed:
      'Snimanje nije bilo moguće zaustaviti. Provjerite vezu ili se ponovno prijavite, a zatim pokušajte ponovno.',
    signOutRecordingTitle: 'Snimanje sastanka još je u tijeku',
    signOutRecordingBody:
      'Odjava ga zaustavlja. Zvuk koji još nije prenesen ostaje na ovom uređaju i prenosi se kad se sljedeći put ovdje prijavite.',
    signOutAnyway: 'Odjava',
    paymentRequired:
      'Kredit za korištenje vaše tvrtke je potrošen. Administrator može dodati karticu, a zatim pokušajte ponovno.',
  },
  cs: {
    pageEyebrow: 'Pracovní prostor {company}',
    pageTitle: 'Schůzky',
    pageDescription:
      'Nahrajte schůzku zde, nebo importujte nahrávku či přepis. Poneva ponechá originál a připraví poznámky se zdroji podle kontextu vaší firmy.',
    loadingMeetings: 'Načítání schůzek…',
    loadFailed: 'Schůzky se nepodařilo načíst.',
    emptyTitle: 'Každá schůzka připravená k použití.',
    emptyDescription:
      'Nahrajte schůzku, nebo importujte nahrávku či přepis. Originál zůstane dostupný, zatímco Poneva připraví přepis a další kroky se zdroji.',
    importMeeting: 'Importovat schůzku',
    importFirst: 'Importujte svou první schůzku',
    importUnavailable:
      'Import zvuku na tomto serveru není k dispozici. Přepisy můžete importovat i nadále.',
    rowRecording: 'Nahrávání… {time}',
    rowSaving: 'Ukládání…',
    importDialogTitle: 'Import schůzky',
    importRetainedNote:
      'Původní nahrávka zůstane zachována. Zvuk se zpracuje do menší řečové kopie, aniž by nahradil originál.',
    importSaved: 'Schůzka je uložena. Přepis probíhá na pozadí.',
    importFailed: 'Tuto schůzku se nepodařilo importovat.',
    uploadProgress: 'Průběh nahrávání',
    uploadingPercent: 'Nahrávání {percent} %',
    titleLabel: 'Název',
    membersLabel: 'Lidé z {company}',
    removeMember: 'Odebrat: {name}',
    searchMembers: 'Hledat členy firmy',
    you: '(vy)',
    noMatchingMembers: 'Žádní odpovídající členové.',
    guestsLabel: 'Další účastníci',
    optional: '(volitelné)',
    guestsPlaceholder: 'Jedno jméno nebo e-mail na řádek',
    cancel: 'Zrušit',
    close: 'Zavřít',
    dismiss: 'Skrýt',
    retryStop: 'Zkusit znovu',
    record: 'Nahrát',
    starting: 'Spouštění…',
    recordDialogTitle: 'Nahrát schůzku',
    recordDialogNote:
      'Zvuk se během nahrávání ukládá do Chatu. Přepis a poznámky vzniknou z nahrávky po zastavení.',
    sourceLabel: 'Co nahrát',
    sourceRoom: 'Tato místnost',
    sourceRoomHint: 'Mikrofon tohoto zařízení.',
    sourceTab: 'Karta se schůzkou',
    sourceTabHint: 'Zvuk hovoru v jiné kartě prohlížeče.',
    sourceBoth: 'Obojí',
    sourceBothHint: 'Karta s hovorem a mikrofon tohoto zařízení.',
    tabPickerNote:
      'Vyberte kartu schůzky a zapněte „Sdílet zvuk karty“. Prohlížeč to nazývá sdílením obrazovky, ale Poneva nahrává pouze zvuk a ihned zastaví video. Pro zahrnutí mikrofonu vyberte „Obojí“.',
    consentLine: 'Všichni účastníci vědí, že se schůzka nahrává.',
    startRecording: 'Spustit nahrávání',
    defaultTitle: 'Schůzka {date}',
    stop: 'Zastavit',
    stopRecordingLabel: 'Zastavit nahrávání ({time})',
    inputLevel: 'Úroveň mikrofonu',
    elapsed: 'Nahrávání {time}',
    uploadBacklog: '{seconds} s zvuku čeká na nahrání.',
    saving: 'Ukládání nahrávky… Zbývá nahrát {seconds} s zvuku.',
    savingOffline:
      'Offline. Nahrávka zůstane v tomto zařízení a nahraje se, až budete znovu online.',
    sealing: 'Ukládání nahrávky',
    recordingNow: 'Probíhá nahrávání',
    recordingElsewhere: 'Tato schůzka se nahrává na jiném zařízení.',
    openRecording: 'Otevřít',
    discardRecording: 'Zahodit nahrávku',
    resume: 'Pokračovat v nahrávání',
    stopHere: 'Zastavit a uložit',
    recoveryTitle: 'Nahrávání bylo přerušeno',
    recoveryBody:
      'Zvuk nahraný před přerušením je uložen. Pokračujte v nahrávání, nebo ho zastavte a uložte.',
    tabEnded: 'Karta se schůzkou se přestala sdílet. Nahrávání pokračuje z mikrofonu.',
    micEnded: 'Mikrofon přestal fungovat. Nahrávání pokračuje z karty se schůzkou.',
    micLost: 'Mikrofon přestal fungovat, a proto bylo nahrávání pozastaveno.',
    limitWarning: 'Nahrávání skončí za {minutes} min.',
    limitReached: 'Nahrávka dosáhla maximální délky a byla zastavena.',
    previewCaption: 'Živý náhled. Konečný přepis vznikne z nahrávky.',
    previewPaused: 'Živý náhled je pozastaven.',
    previewLag: 'Živý text je o {seconds} s pozadu.',
    previewNotShown: '{seconds} s zvuku se nezobrazilo živě. Stále je nahráno.',
    previewUnavailable: 'Živý náhled není k dispozici. Nahrávání pokračuje.',
    micDenied: 'Povolte mikrofon a zkuste to znovu.',
    noMic: 'Nebyl nalezen žádný mikrofon.',
    micBusy: 'Mikrofon používá jiná aplikace.',
    insecureOrigin: 'Nahrávání vyžaduje zabezpečené připojení (https).',
    shareCancelled: 'Sdílení karty bylo zrušeno.',
    shareNoAudio:
      'Vyberte kartu schůzky se zapnutým „Sdílet zvuk karty“. Okno ani celou obrazovku nelze použít pro zvuk karty schůzky.',
    tabUnsupported:
      'Tento prohlížeč neumí nahrávat zvuk karty schůzky. Použijte zde mikrofon, otevřete schůzku a Ponevu v Chromu nebo Edge na počítači, nebo importujte nahrávku.',
    storageLow: 'V zařízení dochází místo. Před nahráváním uvolněte místo.',
    captureClosed: 'Toto nahrávání již bylo zastaveno.',
    captureActiveElsewhere: 'Už nahráváte jinou schůzku.',
    recordUnavailable: 'Nahrávání teď není k dispozici.',
    stopFailed:
      'Nahrávání se nepodařilo zastavit. Zkontrolujte připojení nebo se znovu přihlaste a zkuste to znovu.',
    signOutRecordingTitle: 'Nahrávání schůzky stále probíhá',
    signOutRecordingBody:
      'Odhlášením se zastaví. Zvuk, který ještě nebyl nahrán, zůstane v tomto zařízení a nahraje se při příštím přihlášení zde.',
    signOutAnyway: 'Odhlásit se',
    paymentRequired:
      'Kredit na využití vaší firmy je vyčerpaný. Správce může přidat kartu, pak to zkuste znovu.',
  },
  da: {
    pageEyebrow: 'Arbejdsområde for {company}',
    pageTitle: 'Møder',
    pageDescription:
      'Optag et møde her, eller importér en optagelse eller en transskription. Poneva beholder originalen og udarbejder noter med kildehenvisninger ud fra virksomhedens kontekst.',
    loadingMeetings: 'Indlæser møder…',
    loadFailed: 'Vi kunne ikke indlæse møderne.',
    emptyTitle: 'Hvert møde, klar til brug.',
    emptyDescription:
      'Optag et møde, eller importér en optagelse eller en transskription. Originalen forbliver tilgængelig, mens Poneva udarbejder transskriptionen og opfølgninger med kildehenvisninger.',
    importMeeting: 'Importér møde',
    importFirst: 'Importér dit første møde',
    importUnavailable:
      'Import af lyd er ikke tilgængelig på denne server. Du kan stadig importere transskriptioner.',
    rowRecording: 'Optager… {time}',
    rowSaving: 'Gemmer…',
    importDialogTitle: 'Importér et møde',
    importRetainedNote:
      'Den originale optagelse bevares. Lyden behandles til en mindre talekopi uden at erstatte originalen.',
    importSaved: 'Mødet er gemt. Transskriptionen kører i baggrunden.',
    importFailed: 'Vi kunne ikke importere dette møde.',
    uploadProgress: 'Uploadstatus',
    uploadingPercent: 'Uploader {percent} %',
    titleLabel: 'Titel',
    membersLabel: 'Personer fra {company}',
    removeMember: 'Fjern {name}',
    searchMembers: 'Søg blandt virksomhedens medlemmer',
    you: '(dig)',
    noMatchingMembers: 'Ingen matchende medlemmer.',
    guestsLabel: 'Andre deltagere',
    optional: '(valgfrit)',
    guestsPlaceholder: 'Ét navn eller én e-mailadresse pr. linje',
    cancel: 'Annuller',
    close: 'Luk',
    dismiss: 'Afvis',
    retryStop: 'Prøv igen',
    record: 'Optag',
    starting: 'Starter…',
    recordDialogTitle: 'Optag et møde',
    recordDialogNote:
      'Lyden gemmes i Chat, mens du optager. Transskriptionen og noterne laves ud fra optagelsen, når du har stoppet.',
    sourceLabel: 'Hvad skal optages',
    sourceRoom: 'Dette lokale',
    sourceRoomHint: 'Denne enheds mikrofon.',
    sourceTab: 'En mødefane',
    sourceTabHint: 'Lyden fra et opkald i en anden browserfane.',
    sourceBoth: 'Begge',
    sourceBothHint: 'Opkaldsfanen og denne enheds mikrofon.',
    tabPickerNote:
      'Vælg mødets browserfane, og slå „Del fanelyd“ til. Browseren kalder det skærmdeling, men Poneva optager kun lyd og stopper straks videoen. Vælg „Begge“ for at medtage din mikrofon.',
    consentLine: 'Alle i mødet ved, at det bliver optaget.',
    startRecording: 'Start optagelsen',
    defaultTitle: 'Møde {date}',
    stop: 'Stop',
    stopRecordingLabel: 'Stop optagelsen ({time})',
    inputLevel: 'Mikrofonniveau',
    elapsed: 'Optager {time}',
    uploadBacklog: '{seconds} s lyd venter på at blive uploadet.',
    saving: 'Gemmer optagelsen… {seconds} s lyd mangler at blive uploadet.',
    savingOffline: 'Offline. Optagelsen gemmes på denne enhed og uploades, når du er online igen.',
    sealing: 'Gemmer optagelsen',
    recordingNow: 'Optagelse i gang',
    recordingElsewhere: 'Dette møde bliver optaget på en anden enhed.',
    openRecording: 'Åbn',
    discardRecording: 'Kassér optagelsen',
    resume: 'Genoptag optagelsen',
    stopHere: 'Stop og gem',
    recoveryTitle: 'En optagelse blev afbrudt',
    recoveryBody:
      'Lyden, der blev optaget før afbrydelsen, er gemt. Genoptag for at optage videre, eller stop og gem den.',
    tabEnded: 'Mødefanen holdt op med at blive delt. Optagelsen fortsætter fra mikrofonen.',
    micEnded: 'Mikrofonen stoppede. Optagelsen fortsætter fra mødefanen.',
    micLost: 'Mikrofonen stoppede, så optagelsen blev sat på pause.',
    limitWarning: 'Optagelsen stopper om {minutes} min.',
    limitReached: 'Optagelsen nåede sin maksimale længde og blev stoppet.',
    previewCaption: 'Live-forhåndsvisning. Den endelige transskription laves ud fra optagelsen.',
    previewPaused: 'Live-forhåndsvisningen er sat på pause.',
    previewLag: 'Liveteksten er {seconds} s bagud.',
    previewNotShown: '{seconds} s lyd blev ikke vist live. Den er stadig optaget.',
    previewUnavailable: 'Live-forhåndsvisning er ikke tilgængelig. Optagelsen fortsætter.',
    micDenied: 'Tillad mikrofonen, og prøv igen.',
    noMic: 'Der blev ikke fundet nogen mikrofon.',
    micBusy: 'Mikrofonen bruges af en anden app.',
    insecureOrigin: 'Optagelse kræver en sikker (https) forbindelse.',
    shareCancelled: 'Deling af fanen blev annulleret.',
    shareNoAudio:
      'Vælg mødets browserfane med „Del fanelyd“ slået til. Et vindue eller hele skærmen kan ikke bruges til lyd fra en mødefane.',
    tabUnsupported:
      'Denne browser kan ikke optage lyd fra en mødefane. Brug mikrofonen her, åbn mødet og Poneva i Chrome eller Edge på en computer, eller importér en optagelse.',
    storageLow: 'Enheden har næsten ikke mere lagerplads. Frigør plads, før du optager.',
    captureClosed: 'Denne optagelse er allerede stoppet.',
    captureActiveElsewhere: 'Du optager allerede et andet møde.',
    recordUnavailable: 'Optagelse er ikke tilgængelig lige nu.',
    stopFailed:
      'Optagelsen kunne ikke stoppes. Tjek forbindelsen, eller log ind igen, og prøv så igen.',
    signOutRecordingTitle: 'En mødeoptagelse er stadig i gang',
    signOutRecordingBody:
      'Når du logger ud, stopper den. Lyd, der ikke er uploadet endnu, bliver på denne enhed og uploades, næste gang du logger ind her.',
    signOutAnyway: 'Log ud',
    paymentRequired:
      'Jeres virksomheds forbrugskredit er brugt op. En administrator kan tilføje et kort; prøv derefter igen.',
  },
  nl: {
    pageEyebrow: 'Werkruimte van {company}',
    pageTitle: 'Vergaderingen',
    pageDescription:
      'Neem hier een vergadering op, of importeer een opname of transcript. Poneva bewaart het origineel en maakt notities met bronverwijzingen op basis van de context van je bedrijf.',
    loadingMeetings: 'Vergaderingen laden…',
    loadFailed: 'We konden de vergaderingen niet laden.',
    emptyTitle: 'Elke vergadering, klaar voor gebruik.',
    emptyDescription:
      'Neem een vergadering op, of importeer een opname of transcript. Het origineel blijft beschikbaar terwijl Poneva het transcript en vervolgacties met bronverwijzingen voorbereidt.',
    importMeeting: 'Vergadering importeren',
    importFirst: 'Je eerste vergadering importeren',
    importUnavailable:
      'Audio importeren is niet beschikbaar op deze server. Transcripten kun je nog wel importeren.',
    rowRecording: 'Opnemen… {time}',
    rowSaving: 'Opslaan…',
    importDialogTitle: 'Een vergadering importeren',
    importRetainedNote:
      'De oorspronkelijke opname blijft bewaard. De audio wordt verwerkt tot een kleinere spraakkopie zonder het origineel te vervangen.',
    importSaved: 'Vergadering opgeslagen. De transcriptie loopt op de achtergrond.',
    importFailed: 'We konden deze vergadering niet importeren.',
    uploadProgress: 'Uploadvoortgang',
    uploadingPercent: 'Uploaden {percent}%',
    titleLabel: 'Titel',
    membersLabel: 'Mensen van {company}',
    removeMember: '{name} verwijderen',
    searchMembers: 'Leden van het bedrijf zoeken',
    you: '(jij)',
    noMatchingMembers: 'Geen overeenkomende leden.',
    guestsLabel: 'Andere deelnemers',
    optional: '(optioneel)',
    guestsPlaceholder: 'Eén naam of e-mailadres per regel',
    cancel: 'Annuleren',
    close: 'Sluiten',
    dismiss: 'Negeren',
    retryStop: 'Opnieuw proberen',
    record: 'Opnemen',
    starting: 'Starten…',
    recordDialogTitle: 'Een vergadering opnemen',
    recordDialogNote:
      'De audio wordt tijdens het opnemen in Chat opgeslagen. Het transcript en de notities worden na het stoppen uit de opname gemaakt.',
    sourceLabel: 'Wat opnemen',
    sourceRoom: 'Deze ruimte',
    sourceRoomHint: 'De microfoon van dit apparaat.',
    sourceTab: 'Een vergadertabblad',
    sourceTabHint: 'Het geluid van een gesprek in een ander browsertabblad.',
    sourceBoth: 'Beide',
    sourceBothHint: 'Het gesprekstabblad en de microfoon van dit apparaat.',
    tabPickerNote:
      'Kies het browsertabblad van de vergadering en zet ‘Tabbladaudio delen’ aan. De browser noemt dit scherm delen, maar Poneva neemt alleen audio op en stopt de video meteen. Kies ‘Beide’ om je microfoon mee te nemen.',
    consentLine: 'Iedereen in de vergadering weet dat er wordt opgenomen.',
    startRecording: 'Opname starten',
    defaultTitle: 'Vergadering {date}',
    stop: 'Stoppen',
    stopRecordingLabel: 'Opname stoppen ({time})',
    inputLevel: 'Microfoonniveau',
    elapsed: 'Opname {time}',
    uploadBacklog: '{seconds} s audio wacht op upload.',
    saving: 'Opname opslaan… Nog {seconds} s audio te uploaden.',
    savingOffline:
      'Offline. De opname blijft op dit apparaat en wordt geüpload zodra je weer verbinding hebt.',
    sealing: 'Opname opslaan',
    recordingNow: 'Opname bezig',
    recordingElsewhere: 'Deze vergadering wordt op een ander apparaat opgenomen.',
    openRecording: 'Openen',
    discardRecording: 'Opname verwijderen',
    resume: 'Opname hervatten',
    stopHere: 'Stoppen en opslaan',
    recoveryTitle: 'Een opname is onderbroken',
    recoveryBody:
      'De audio van vóór de onderbreking is opgeslagen. Hervat de opname, of stop en sla hem op.',
    tabEnded:
      'Het vergadertabblad wordt niet meer gedeeld. De opname gaat verder via de microfoon.',
    micEnded: 'De microfoon is gestopt. De opname gaat verder via het vergadertabblad.',
    micLost: 'De microfoon is gestopt, dus de opname is gepauzeerd.',
    limitWarning: 'De opname stopt over {minutes} min.',
    limitReached: 'De opname heeft de maximale lengte bereikt en is gestopt.',
    previewCaption: 'Livevoorbeeld. Het definitieve transcript komt uit de opname.',
    previewPaused: 'Livevoorbeeld gepauzeerd.',
    previewLag: 'De livetekst loopt {seconds} s achter.',
    previewNotShown: '{seconds} s audio is niet live getoond. Het is wel opgenomen.',
    previewUnavailable: 'Het livevoorbeeld is niet beschikbaar. De opname gaat door.',
    micDenied: 'Geef toegang tot de microfoon en probeer het opnieuw.',
    noMic: 'Er is geen microfoon gevonden.',
    micBusy: 'De microfoon wordt door een andere app gebruikt.',
    insecureOrigin: 'Opnemen vereist een beveiligde (https) verbinding.',
    shareCancelled: 'Het delen van het tabblad is geannuleerd.',
    shareNoAudio:
      'Kies het vergadertabblad met ‘Tabbladaudio delen’ ingeschakeld. Een venster of het hele scherm kan niet worden gebruikt voor audio van een vergadertabblad.',
    tabUnsupported:
      'Deze browser kan geen audio van een vergadertabblad opnemen. Gebruik hier de microfoon, open de vergadering en Poneva in Chrome of Edge op een computer, of importeer een opname.',
    storageLow: 'Dit apparaat heeft weinig opslagruimte. Maak ruimte vrij voordat je opneemt.',
    captureClosed: 'Deze opname is al gestopt.',
    captureActiveElsewhere: 'Je neemt al een andere vergadering op.',
    recordUnavailable: 'Opnemen is nu niet beschikbaar.',
    stopFailed:
      'De opname kon niet worden gestopt. Controleer je verbinding of meld je opnieuw aan en probeer het dan opnieuw.',
    signOutRecordingTitle: 'Er loopt nog een vergaderopname',
    signOutRecordingBody:
      'Afmelden stopt de opname. Audio die nog niet is geüpload, blijft op dit apparaat en wordt geüpload zodra je je hier weer aanmeldt.',
    signOutAnyway: 'Afmelden',
    paymentRequired:
      'Het gebruikstegoed van je bedrijf is op. Een beheerder kan een kaart toevoegen; probeer het daarna opnieuw.',
  },
  en,
  et: {
    pageEyebrow: '{company} tööruum',
    pageTitle: 'Koosolekud',
    pageDescription:
      'Salvesta koosolek siin või impordi salvestis või transkriptsioon. Poneva säilitab originaali ja koostab ettevõtte konteksti põhjal viidetega märkmed.',
    loadingMeetings: 'Koosolekute laadimine…',
    loadFailed: 'Koosolekuid ei õnnestunud laadida.',
    emptyTitle: 'Iga koosolek kasutusvalmis.',
    emptyDescription:
      'Salvesta koosolek või impordi salvestis või transkriptsioon. Originaal jääb kättesaadavaks, kuni Poneva koostab transkriptsiooni ja viidetega järeltegevused.',
    importMeeting: 'Impordi koosolek',
    importFirst: 'Impordi oma esimene koosolek',
    importUnavailable:
      'Heli importimine pole selles serveris saadaval. Transkriptsioone saad endiselt importida.',
    rowRecording: 'Salvestamine… {time}',
    rowSaving: 'Salvestamine…',
    importDialogTitle: 'Koosoleku importimine',
    importRetainedNote:
      'Algne salvestis säilitatakse. Heli töödeldakse väiksemaks kõnekoopiaks originaali asendamata.',
    importSaved: 'Koosolek on salvestatud. Transkribeerimine käib taustal.',
    importFailed: 'Seda koosolekut ei õnnestunud importida.',
    uploadProgress: 'Üleslaadimise edenemine',
    uploadingPercent: 'Üleslaadimine {percent}%',
    titleLabel: 'Pealkiri',
    membersLabel: 'Inimesed ettevõttest {company}',
    removeMember: 'Eemalda {name}',
    searchMembers: 'Otsi ettevõtte liikmeid',
    you: '(sina)',
    noMatchingMembers: 'Sobivaid liikmeid ei leitud.',
    guestsLabel: 'Teised osalejad',
    optional: '(valikuline)',
    guestsPlaceholder: 'Üks nimi või e-posti aadress rea kohta',
    cancel: 'Tühista',
    close: 'Sulge',
    dismiss: 'Loobu',
    retryStop: 'Proovi uuesti',
    record: 'Salvesta',
    starting: 'Käivitamine…',
    recordDialogTitle: 'Koosoleku salvestamine',
    recordDialogNote:
      'Heli salvestatakse salvestamise ajal Chati. Transkriptsioon ja märkmed koostatakse salvestisest pärast peatamist.',
    sourceLabel: 'Mida salvestada',
    sourceRoom: 'See ruum',
    sourceRoomHint: 'Selle seadme mikrofon.',
    sourceTab: 'Koosoleku vahekaart',
    sourceTabHint: 'Kõne heli teisest brauseri vahekaardist.',
    sourceBoth: 'Mõlemad',
    sourceBothHint: 'Kõne vahekaart ja selle seadme mikrofon.',
    tabPickerNote:
      'Vali koosoleku brauserivahekaart ja lülita sisse „Jaga vahekaardi heli“. Brauser nimetab seda ekraani jagamiseks, kuid Poneva salvestab ainult heli ja peatab video kohe. Mikrofoni kaasamiseks vali „Mõlemad“.',
    consentLine: 'Kõik koosolekul osalejad teavad, et seda salvestatakse.',
    startRecording: 'Alusta salvestamist',
    defaultTitle: 'Koosolek {date}',
    stop: 'Peata',
    stopRecordingLabel: 'Peata salvestamine ({time})',
    inputLevel: 'Mikrofoni tase',
    elapsed: 'Salvestamine {time}',
    uploadBacklog: '{seconds} s heli ootab üleslaadimist.',
    saving: 'Salvestise salvestamine… {seconds} s heli on veel üles laadimata.',
    savingOffline:
      'Ühendus puudub. Salvestis jääb sellesse seadmesse ja laaditakse üles, kui ühendus taastub.',
    sealing: 'Salvestise salvestamine',
    recordingNow: 'Salvestamine käib',
    recordingElsewhere: 'Seda koosolekut salvestatakse teises seadmes.',
    openRecording: 'Ava',
    discardRecording: 'Loobu salvestisest',
    resume: 'Jätka salvestamist',
    stopHere: 'Peata ja salvesta',
    recoveryTitle: 'Salvestamine katkes',
    recoveryBody:
      'Enne katkestust salvestatud heli on alles. Jätka salvestamist või peata ja salvesta see.',
    tabEnded: 'Koosoleku vahekaardi jagamine lõppes. Salvestamine jätkub mikrofonist.',
    micEnded: 'Mikrofon lakkas töötamast. Salvestamine jätkub koosoleku vahekaardilt.',
    micLost: 'Mikrofon lakkas töötamast, seega salvestamine peatati.',
    limitWarning: 'Salvestamine peatub {minutes} min pärast.',
    limitReached: 'Salvestis jõudis maksimaalse pikkuseni ja peatati.',
    previewCaption: 'Reaalajas eelvaade. Lõplik transkriptsioon tehakse salvestisest.',
    previewPaused: 'Reaalajas eelvaade on peatatud.',
    previewLag: 'Reaalajas tekst on {seconds} s maas.',
    previewNotShown: '{seconds} s heli ei näidatud reaalajas. See on siiski salvestatud.',
    previewUnavailable: 'Reaalajas eelvaade pole saadaval. Salvestamine jätkub.',
    micDenied: 'Luba mikrofon ja proovi uuesti.',
    noMic: 'Mikrofoni ei leitud.',
    micBusy: 'Mikrofoni kasutab teine rakendus.',
    insecureOrigin: 'Salvestamiseks on vaja turvalist (https) ühendust.',
    shareCancelled: 'Vahekaardi jagamine tühistati.',
    shareNoAudio:
      'Vali koosoleku vahekaart, kus „Jaga vahekaardi heli“ on sisse lülitatud. Akent või tervet ekraani ei saa vahekaardi heli jaoks kasutada.',
    tabUnsupported:
      'See brauser ei saa koosoleku vahekaardi heli salvestada. Kasuta siin mikrofoni, ava koosolek ja Poneva arvutis Chrome’is või Edge’is või impordi salvestis.',
    storageLow: 'Seadmes on vähe salvestusruumi. Vabasta enne salvestamist ruumi.',
    captureClosed: 'See salvestamine on juba peatatud.',
    captureActiveElsewhere: 'Sa juba salvestad teist koosolekut.',
    recordUnavailable: 'Salvestamine pole praegu saadaval.',
    stopFailed:
      'Salvestust ei õnnestunud peatada. Kontrolli ühendust või logi uuesti sisse ja proovi siis uuesti.',
    signOutRecordingTitle: 'Koosoleku salvestamine on veel pooleli',
    signOutRecordingBody:
      'Väljalogimine peatab selle. Veel üles laadimata heli jääb sellesse seadmesse ja laaditakse üles, kui järgmine kord siin sisse logid.',
    signOutAnyway: 'Logi välja',
    paymentRequired:
      'Ettevõtte kasutuskrediit on otsas. Administraator saab lisada kaardi, seejärel proovi uuesti.',
  },
  fi: {
    pageEyebrow: 'Työtila: {company}',
    pageTitle: 'Kokoukset',
    pageDescription:
      'Tallenna kokous täällä tai tuo tallenne tai litterointi. Poneva säilyttää alkuperäisen ja laatii lähdeviitteelliset muistiinpanot yrityksesi kontekstin avulla.',
    loadingMeetings: 'Ladataan kokouksia…',
    loadFailed: 'Kokouksia ei voitu ladata.',
    emptyTitle: 'Jokainen kokous käyttövalmiina.',
    emptyDescription:
      'Tallenna kokous tai tuo tallenne tai litterointi. Alkuperäinen pysyy saatavilla, kun Poneva laatii litteroinnin ja lähdeviitteelliset jatkotoimet.',
    importMeeting: 'Tuo kokous',
    importFirst: 'Tuo ensimmäinen kokouksesi',
    importUnavailable:
      'Äänen tuonti ei ole käytettävissä tällä palvelimella. Voit silti tuoda litterointeja.',
    rowRecording: 'Tallennetaan… {time}',
    rowSaving: 'Tallennetaan…',
    importDialogTitle: 'Tuo kokous',
    importRetainedNote:
      'Alkuperäinen tallenne säilytetään. Ääni käsitellään pienemmäksi puhekopioksi korvaamatta alkuperäistä.',
    importSaved: 'Kokous tallennettiin. Litterointi on käynnissä taustalla.',
    importFailed: 'Tätä kokousta ei voitu tuoda.',
    uploadProgress: 'Latauksen edistyminen',
    uploadingPercent: 'Ladataan {percent} %',
    titleLabel: 'Otsikko',
    membersLabel: 'Henkilöt: {company}',
    removeMember: 'Poista {name}',
    searchMembers: 'Hae yrityksen jäseniä',
    you: '(sinä)',
    noMatchingMembers: 'Ei vastaavia jäseniä.',
    guestsLabel: 'Muut osallistujat',
    optional: '(valinnainen)',
    guestsPlaceholder: 'Yksi nimi tai sähköpostiosoite riville',
    cancel: 'Peruuta',
    close: 'Sulje',
    dismiss: 'Ohita',
    retryStop: 'Yritä uudelleen',
    record: 'Tallenna',
    starting: 'Käynnistetään…',
    recordDialogTitle: 'Tallenna kokous',
    recordDialogNote:
      'Ääni tallennetaan Chattiin tallennuksen aikana. Litterointi ja muistiinpanot laaditaan tallenteesta, kun lopetat.',
    sourceLabel: 'Mitä tallennetaan',
    sourceRoom: 'Tämä huone',
    sourceRoomHint: 'Tämän laitteen mikrofoni.',
    sourceTab: 'Kokousvälilehti',
    sourceTabHint: 'Puhelun ääni toisesta selaimen välilehdestä.',
    sourceBoth: 'Molemmat',
    sourceBothHint: 'Puheluvälilehti ja tämän laitteen mikrofoni.',
    tabPickerNote:
      'Valitse kokouksen selainvälilehti ja ota „Jaa välilehden ääni“ käyttöön. Selain kutsuu tätä näytön jakamiseksi, mutta Poneva tallentaa vain äänen ja pysäyttää videon heti. Valitse „Molemmat“, jos haluat mikrofonin mukaan.',
    consentLine: 'Kaikki kokouksessa tietävät, että sitä tallennetaan.',
    startRecording: 'Aloita tallennus',
    defaultTitle: 'Kokous {date}',
    stop: 'Lopeta',
    stopRecordingLabel: 'Lopeta tallennus ({time})',
    inputLevel: 'Mikrofonin taso',
    elapsed: 'Tallennetaan {time}',
    uploadBacklog: '{seconds} s ääntä odottaa latausta.',
    saving: 'Tallennetaan tallennetta… {seconds} s ääntä vielä lataamatta.',
    savingOffline: 'Ei yhteyttä. Tallenne säilyy tällä laitteella ja ladataan, kun yhteys palaa.',
    sealing: 'Tallennetaan tallennetta',
    recordingNow: 'Tallennus käynnissä',
    recordingElsewhere: 'Tätä kokousta tallennetaan toisella laitteella.',
    openRecording: 'Avaa',
    discardRecording: 'Hylkää tallenne',
    resume: 'Jatka tallennusta',
    stopHere: 'Lopeta ja tallenna',
    recoveryTitle: 'Tallennus keskeytyi',
    recoveryBody:
      'Ennen keskeytystä tallennettu ääni on tallessa. Jatka tallennusta tai lopeta ja tallenna se.',
    tabEnded: 'Kokousvälilehden jakaminen päättyi. Tallennus jatkuu mikrofonista.',
    micEnded: 'Mikrofoni lakkasi toimimasta. Tallennus jatkuu kokousvälilehdeltä.',
    micLost: 'Mikrofoni lakkasi toimimasta, joten tallennus keskeytettiin.',
    limitWarning: 'Tallennus päättyy {minutes} minuutin kuluttua.',
    limitReached: 'Tallennus saavutti enimmäispituutensa ja lopetettiin.',
    previewCaption: 'Reaaliaikainen esikatselu. Lopullinen litterointi tehdään tallenteesta.',
    previewPaused: 'Reaaliaikainen esikatselu on keskeytetty.',
    previewLag: 'Reaaliaikainen teksti on {seconds} s jäljessä.',
    previewNotShown: '{seconds} s ääntä ei näytetty reaaliajassa. Se on silti tallennettu.',
    previewUnavailable: 'Reaaliaikainen esikatselu ei ole käytettävissä. Tallennus jatkuu.',
    micDenied: 'Salli mikrofoni ja yritä uudelleen.',
    noMic: 'Mikrofonia ei löytynyt.',
    micBusy: 'Toinen sovellus käyttää mikrofonia.',
    insecureOrigin: 'Tallennus edellyttää suojattua (https) yhteyttä.',
    shareCancelled: 'Välilehden jakaminen peruttiin.',
    shareNoAudio:
      'Valitse kokousvälilehti ja ota „Jaa välilehden ääni“ käyttöön. Ikkunaa tai koko näyttöä ei voi käyttää kokousvälilehden ääneen.',
    tabUnsupported:
      'Tämä selain ei voi tallentaa kokousvälilehden ääntä. Käytä tässä mikrofonia, avaa kokous ja Poneva tietokoneen Chromessa tai Edgessä tai tuo tallenne.',
    storageLow: 'Laitteen tallennustila on vähissä. Vapauta tilaa ennen tallennusta.',
    captureClosed: 'Tämä tallennus on jo lopetettu.',
    captureActiveElsewhere: 'Tallennat jo toista kokousta.',
    recordUnavailable: 'Tallennus ei ole käytettävissä juuri nyt.',
    stopFailed:
      'Tallennusta ei voitu pysäyttää. Tarkista yhteys tai kirjaudu uudelleen sisään ja yritä sitten uudelleen.',
    signOutRecordingTitle: 'Kokouksen tallennus on vielä kesken',
    signOutRecordingBody:
      'Uloskirjautuminen pysäyttää sen. Ääni, jota ei ole vielä lähetetty, jää tälle laitteelle ja lähetetään, kun kirjaudut seuraavan kerran sisään täällä.',
    signOutAnyway: 'Kirjaudu ulos',
    paymentRequired:
      'Yrityksesi käyttösaldo on käytetty. Ylläpitäjä voi lisätä kortin, yritä sitten uudelleen.',
  },
  fr: {
    pageEyebrow: 'Espace de travail {company}',
    pageTitle: 'Réunions',
    pageDescription:
      'Enregistrez une réunion ici, ou importez un enregistrement ou une transcription. Poneva conserve l’original et prépare des notes sourcées à partir du contexte de votre entreprise.',
    loadingMeetings: 'Chargement des réunions…',
    loadFailed: 'Impossible de charger les réunions.',
    emptyTitle: 'Chaque réunion, prête à l’emploi.',
    emptyDescription:
      'Enregistrez une réunion, ou importez un enregistrement ou une transcription. L’original reste disponible pendant que Poneva prépare la transcription et les suites à donner, avec leurs sources.',
    importMeeting: 'Importer une réunion',
    importFirst: 'Importer votre première réunion',
    importUnavailable:
      'L’import audio n’est pas disponible sur ce serveur. Vous pouvez toujours importer des transcriptions.',
    rowRecording: 'Enregistrement… {time}',
    rowSaving: 'Enregistrement en cours…',
    importDialogTitle: 'Importer une réunion',
    importRetainedNote:
      'L’enregistrement d’origine est conservé. L’audio est converti en une copie vocale plus légère sans remplacer l’original.',
    importSaved: 'Réunion enregistrée. La transcription se poursuit en arrière-plan.',
    importFailed: 'Impossible d’importer cette réunion.',
    uploadProgress: 'Progression de l’envoi',
    uploadingPercent: 'Envoi : {percent} %',
    titleLabel: 'Titre',
    membersLabel: 'Personnes de {company}',
    removeMember: 'Retirer {name}',
    searchMembers: 'Rechercher des membres de l’entreprise',
    you: '(vous)',
    noMatchingMembers: 'Aucun membre correspondant.',
    guestsLabel: 'Autres participants',
    optional: '(facultatif)',
    guestsPlaceholder: 'Un nom ou une adresse e-mail par ligne',
    cancel: 'Annuler',
    close: 'Fermer',
    dismiss: 'Ignorer',
    retryStop: 'Réessayer',
    record: 'Enregistrer',
    starting: 'Démarrage…',
    recordDialogTitle: 'Enregistrer une réunion',
    recordDialogNote:
      'L’audio est sauvegardé dans Chat pendant l’enregistrement. La transcription et les notes sont produites à partir de l’enregistrement une fois arrêté.',
    sourceLabel: 'Que faut-il enregistrer',
    sourceRoom: 'Cette salle',
    sourceRoomHint: 'Le micro de cet appareil.',
    sourceTab: 'Un onglet de réunion',
    sourceTabHint: 'Le son d’un appel dans un autre onglet du navigateur.',
    sourceBoth: 'Les deux',
    sourceBothHint: 'L’onglet de l’appel et le micro de cet appareil.',
    tabPickerNote:
      'Choisissez l’onglet de la réunion et activez « Partager l’audio de l’onglet ». Le navigateur parle de partage d’écran, mais Poneva enregistre uniquement l’audio et arrête immédiatement la vidéo. Choisissez « Les deux » pour inclure votre micro.',
    consentLine: 'Tous les participants savent que la réunion est enregistrée.',
    startRecording: 'Démarrer l’enregistrement',
    defaultTitle: 'Réunion {date}',
    stop: 'Arrêter',
    stopRecordingLabel: 'Arrêter l’enregistrement ({time})',
    inputLevel: 'Niveau du micro',
    elapsed: 'Enregistrement {time}',
    uploadBacklog: '{seconds} s d’audio en attente d’envoi.',
    saving: 'Sauvegarde de l’enregistrement… Encore {seconds} s d’audio à envoyer.',
    savingOffline:
      'Hors ligne. L’enregistrement reste sur cet appareil et sera envoyé dès que vous serez reconnecté.',
    sealing: 'Sauvegarde de l’enregistrement',
    recordingNow: 'Enregistrement en cours',
    recordingElsewhere: 'Cette réunion est enregistrée sur un autre appareil.',
    openRecording: 'Ouvrir',
    discardRecording: 'Supprimer l’enregistrement',
    resume: 'Reprendre l’enregistrement',
    stopHere: 'Arrêter et sauvegarder',
    recoveryTitle: 'Un enregistrement a été interrompu',
    recoveryBody:
      'L’audio enregistré avant l’interruption est sauvegardé. Reprenez l’enregistrement, ou arrêtez et sauvegardez-le.',
    tabEnded: 'L’onglet de réunion n’est plus partagé. L’enregistrement continue avec le micro.',
    micEnded: 'Le micro s’est arrêté. L’enregistrement continue avec l’onglet de réunion.',
    micLost: 'Le micro s’est arrêté, l’enregistrement a donc été mis en pause.',
    limitWarning: 'L’enregistrement s’arrête dans {minutes} min.',
    limitReached: 'L’enregistrement a atteint sa durée maximale et a été arrêté.',
    previewCaption:
      'Aperçu en direct. La transcription finale est produite à partir de l’enregistrement.',
    previewPaused: 'Aperçu en direct en pause.',
    previewLag: 'Le texte en direct a {seconds} s de retard.',
    previewNotShown:
      '{seconds} s d’audio n’ont pas été affichées en direct. Elles sont bien enregistrées.',
    previewUnavailable: 'L’aperçu en direct n’est pas disponible. L’enregistrement continue.',
    micDenied: 'Autorisez le micro et réessayez.',
    noMic: 'Aucun micro n’a été trouvé.',
    micBusy: 'Le micro est utilisé par une autre application.',
    insecureOrigin: 'L’enregistrement nécessite une connexion sécurisée (https).',
    shareCancelled: 'Le partage de l’onglet a été annulé.',
    shareNoAudio:
      'Choisissez l’onglet de la réunion avec « Partager l’audio de l’onglet » activé. Une fenêtre ou l’écran entier ne permet pas de capturer l’audio d’un onglet.',
    tabUnsupported:
      'Ce navigateur ne peut pas capturer l’audio d’un onglet de réunion. Utilisez le micro ici, ouvrez la réunion et Poneva dans Chrome ou Edge sur ordinateur, ou importez un enregistrement.',
    storageLow:
      'L’espace de stockage de cet appareil est presque plein. Libérez de l’espace avant d’enregistrer.',
    captureClosed: 'Cet enregistrement a déjà été arrêté.',
    captureActiveElsewhere: 'Vous enregistrez déjà une autre réunion.',
    recordUnavailable: 'L’enregistrement n’est pas disponible pour le moment.',
    stopFailed:
      'L’enregistrement n’a pas pu être arrêté. Vérifiez votre connexion ou reconnectez-vous, puis réessayez.',
    signOutRecordingTitle: 'L’enregistrement d’une réunion est encore en cours',
    signOutRecordingBody:
      'La déconnexion l’arrête. L’audio pas encore envoyé reste sur cet appareil et finit d’être envoyé à votre prochaine connexion ici.',
    signOutAnyway: 'Se déconnecter',
    paymentRequired:
      'Le crédit d’utilisation de votre entreprise est épuisé. Un administrateur peut ajouter une carte ; réessayez ensuite.',
  },
  de: {
    pageEyebrow: 'Arbeitsbereich von {company}',
    pageTitle: 'Meetings',
    pageDescription:
      'Nehmen Sie hier ein Meeting auf oder importieren Sie eine Aufnahme oder ein Transkript. Poneva bewahrt das Original auf und erstellt zitierte Notizen mit dem Kontext Ihres Unternehmens.',
    loadingMeetings: 'Meetings werden geladen…',
    loadFailed: 'Die Meetings konnten nicht geladen werden.',
    emptyTitle: 'Jedes Meeting, bereit zur Nutzung.',
    emptyDescription:
      'Nehmen Sie ein Meeting auf oder importieren Sie eine Aufnahme oder ein Transkript. Das Original bleibt verfügbar, während Poneva das Transkript und Folgeaufgaben mit Quellenangaben erstellt.',
    importMeeting: 'Meeting importieren',
    importFirst: 'Erstes Meeting importieren',
    importUnavailable:
      'Der Import von Audiodateien ist auf diesem Server nicht verfügbar. Sie können weiterhin Transkripte importieren.',
    rowRecording: 'Aufnahme läuft… {time}',
    rowSaving: 'Wird gespeichert…',
    importDialogTitle: 'Ein Meeting importieren',
    importRetainedNote:
      'Die Originalaufnahme bleibt erhalten. Das Audio wird zu einer kleineren Sprachkopie verarbeitet, ohne das Original zu ersetzen.',
    importSaved: 'Meeting gespeichert. Die Transkription läuft im Hintergrund.',
    importFailed: 'Dieses Meeting konnte nicht importiert werden.',
    uploadProgress: 'Upload-Fortschritt',
    uploadingPercent: 'Wird hochgeladen: {percent} %',
    titleLabel: 'Titel',
    membersLabel: 'Personen von {company}',
    removeMember: '{name} entfernen',
    searchMembers: 'Mitglieder des Unternehmens suchen',
    you: '(Sie)',
    noMatchingMembers: 'Keine passenden Mitglieder.',
    guestsLabel: 'Weitere Teilnehmende',
    optional: '(optional)',
    guestsPlaceholder: 'Ein Name oder eine E-Mail-Adresse pro Zeile',
    cancel: 'Abbrechen',
    close: 'Schließen',
    dismiss: 'Ausblenden',
    retryStop: 'Erneut versuchen',
    record: 'Aufnehmen',
    starting: 'Wird gestartet…',
    recordDialogTitle: 'Ein Meeting aufnehmen',
    recordDialogNote:
      'Das Audio wird während der Aufnahme in Chat gespeichert. Transkript und Notizen entstehen nach dem Stoppen aus der Aufnahme.',
    sourceLabel: 'Was aufgenommen wird',
    sourceRoom: 'Dieser Raum',
    sourceRoomHint: 'Das Mikrofon dieses Geräts.',
    sourceTab: 'Ein Meeting-Tab',
    sourceTabHint: 'Der Ton eines Anrufs in einem anderen Browser-Tab.',
    sourceBoth: 'Beides',
    sourceBothHint: 'Der Anruf-Tab und das Mikrofon dieses Geräts.',
    tabPickerNote:
      'Wählen Sie den Browser-Tab des Meetings aus und aktivieren Sie „Tab-Audio teilen“. Der Browser bezeichnet dies als Bildschirmfreigabe, aber Poneva nimmt nur Audio auf und stoppt das Video sofort. Wählen Sie „Beide“, um Ihr Mikrofon einzubeziehen.',
    consentLine: 'Alle im Meeting wissen, dass es aufgenommen wird.',
    startRecording: 'Aufnahme starten',
    defaultTitle: 'Meeting {date}',
    stop: 'Stopp',
    stopRecordingLabel: 'Aufnahme stoppen ({time})',
    inputLevel: 'Mikrofonpegel',
    elapsed: 'Aufnahme {time}',
    uploadBacklog: '{seconds} s Audio warten auf den Upload.',
    saving: 'Aufnahme wird gespeichert… Noch {seconds} s Audio hochzuladen.',
    savingOffline:
      'Offline. Die Aufnahme wird auf diesem Gerät gespeichert und hochgeladen, sobald Sie wieder verbunden sind.',
    sealing: 'Aufnahme wird gespeichert',
    recordingNow: 'Aufnahme läuft',
    recordingElsewhere: 'Dieses Meeting wird auf einem anderen Gerät aufgenommen.',
    openRecording: 'Öffnen',
    discardRecording: 'Aufnahme verwerfen',
    resume: 'Aufnahme fortsetzen',
    stopHere: 'Stoppen und speichern',
    recoveryTitle: 'Eine Aufnahme wurde unterbrochen',
    recoveryBody:
      'Das vor der Unterbrechung aufgenommene Audio ist gespeichert. Setzen Sie die Aufnahme fort oder stoppen und speichern Sie sie.',
    tabEnded:
      'Der Meeting-Tab wird nicht mehr geteilt. Die Aufnahme läuft über das Mikrofon weiter.',
    micEnded: 'Das Mikrofon ist ausgefallen. Die Aufnahme läuft über den Meeting-Tab weiter.',
    micLost: 'Das Mikrofon ist ausgefallen, daher wurde die Aufnahme pausiert.',
    limitWarning: 'Die Aufnahme endet in {minutes} Min.',
    limitReached: 'Die Aufnahme hat ihre maximale Länge erreicht und wurde gestoppt.',
    previewCaption: 'Live-Vorschau. Das endgültige Transkript entsteht aus der Aufnahme.',
    previewPaused: 'Live-Vorschau pausiert.',
    previewLag: 'Der Live-Text liegt {seconds} s zurück.',
    previewNotShown:
      '{seconds} s Audio wurden nicht live angezeigt. Sie sind trotzdem aufgenommen.',
    previewUnavailable: 'Die Live-Vorschau ist nicht verfügbar. Die Aufnahme läuft weiter.',
    micDenied: 'Erlauben Sie den Zugriff auf das Mikrofon und versuchen Sie es erneut.',
    noMic: 'Es wurde kein Mikrofon gefunden.',
    micBusy: 'Das Mikrofon wird von einer anderen App verwendet.',
    insecureOrigin: 'Für die Aufnahme ist eine sichere (https) Verbindung nötig.',
    shareCancelled: 'Das Teilen des Tabs wurde abgebrochen.',
    shareNoAudio:
      'Wählen Sie den Browser-Tab des Meetings mit aktiviertem „Tab-Audio teilen“. Ein Fenster oder der gesamte Bildschirm kann nicht für den Audio-Stream des Meeting-Tabs verwendet werden.',
    tabUnsupported:
      'Dieser Browser kann kein Meeting-Tab-Audio aufnehmen. Verwenden Sie hier das Mikrofon, öffnen Sie das Meeting und Poneva in Chrome oder Edge am Computer oder importieren Sie eine Aufnahme.',
    storageLow:
      'Auf diesem Gerät ist nur noch wenig Speicherplatz verfügbar. Geben Sie vor der Aufnahme Speicherplatz frei.',
    captureClosed: 'Diese Aufnahme wurde bereits gestoppt.',
    captureActiveElsewhere: 'Sie nehmen bereits ein anderes Meeting auf.',
    recordUnavailable: 'Aufnehmen ist gerade nicht verfügbar.',
    stopFailed:
      'Die Aufnahme konnte nicht beendet werden. Überprüfen Sie Ihre Verbindung oder melden Sie sich erneut an. Versuchen Sie es anschließend noch einmal.',
    signOutRecordingTitle: 'Eine Besprechungsaufnahme läuft noch',
    signOutRecordingBody:
      'Durch das Abmelden wird die Aufnahme beendet. Noch nicht hochgeladene Audiodaten bleiben auf diesem Gerät und werden bei Ihrer nächsten Anmeldung hier fertig hochgeladen.',
    signOutAnyway: 'Abmelden',
    paymentRequired:
      'Das Nutzungsguthaben Ihres Unternehmens ist aufgebraucht. Ein Administrator kann eine Karte hinzufügen; versuchen Sie es dann erneut.',
  },
  el: {
    pageEyebrow: 'Χώρος εργασίας {company}',
    pageTitle: 'Συσκέψεις',
    pageDescription:
      'Ηχογραφήστε μια σύσκεψη εδώ ή εισαγάγετε μια ηχογράφηση ή απομαγνητοφώνηση. Το Poneva κρατά το πρωτότυπο και ετοιμάζει σημειώσεις με πηγές με βάση το πλαίσιο της εταιρείας σας.',
    loadingMeetings: 'Φόρτωση συσκέψεων…',
    loadFailed: 'Δεν ήταν δυνατή η φόρτωση των συσκέψεων.',
    emptyTitle: 'Κάθε σύσκεψη, έτοιμη για χρήση.',
    emptyDescription:
      'Ηχογραφήστε μια σύσκεψη ή εισαγάγετε μια ηχογράφηση ή απομαγνητοφώνηση. Το πρωτότυπο παραμένει διαθέσιμο ενώ το Poneva ετοιμάζει την απομαγνητοφώνηση και τις επόμενες ενέργειες με πηγές.',
    importMeeting: 'Εισαγωγή σύσκεψης',
    importFirst: 'Εισαγάγετε την πρώτη σας σύσκεψη',
    importUnavailable:
      'Η εισαγωγή ήχου δεν είναι διαθέσιμη σε αυτόν τον διακομιστή. Μπορείτε ακόμη να εισαγάγετε απομαγνητοφωνήσεις.',
    rowRecording: 'Ηχογράφηση… {time}',
    rowSaving: 'Αποθήκευση…',
    importDialogTitle: 'Εισαγωγή σύσκεψης',
    importRetainedNote:
      'Η αρχική ηχογράφηση διατηρείται. Ο ήχος μετατρέπεται σε μικρότερο αντίγραφο ομιλίας χωρίς να αντικαθιστά το πρωτότυπο.',
    importSaved: 'Η σύσκεψη αποθηκεύτηκε. Η απομαγνητοφώνηση εκτελείται στο παρασκήνιο.',
    importFailed: 'Δεν ήταν δυνατή η εισαγωγή αυτής της σύσκεψης.',
    uploadProgress: 'Πρόοδος αποστολής',
    uploadingPercent: 'Αποστολή {percent}%',
    titleLabel: 'Τίτλος',
    membersLabel: 'Άτομα από {company}',
    removeMember: 'Αφαίρεση: {name}',
    searchMembers: 'Αναζήτηση μελών της εταιρείας',
    you: '(εσείς)',
    noMatchingMembers: 'Δεν βρέθηκαν μέλη.',
    guestsLabel: 'Άλλοι συμμετέχοντες',
    optional: '(προαιρετικό)',
    guestsPlaceholder: 'Ένα όνομα ή email ανά γραμμή',
    cancel: 'Ακύρωση',
    close: 'Κλείσιμο',
    dismiss: 'Απόρριψη',
    retryStop: 'Δοκιμάστε ξανά',
    record: 'Ηχογράφηση',
    starting: 'Εκκίνηση…',
    recordDialogTitle: 'Ηχογράφηση σύσκεψης',
    recordDialogNote:
      'Ο ήχος αποθηκεύεται στο Chat όσο ηχογραφείτε. Η απομαγνητοφώνηση και οι σημειώσεις δημιουργούνται από την ηχογράφηση αφού σταματήσετε.',
    sourceLabel: 'Τι θα ηχογραφηθεί',
    sourceRoom: 'Αυτή η αίθουσα',
    sourceRoomHint: 'Το μικρόφωνο αυτής της συσκευής.',
    sourceTab: 'Μια καρτέλα σύσκεψης',
    sourceTabHint: 'Ο ήχος μιας κλήσης σε άλλη καρτέλα του προγράμματος περιήγησης.',
    sourceBoth: 'Και τα δύο',
    sourceBothHint: 'Η καρτέλα της κλήσης και το μικρόφωνο αυτής της συσκευής.',
    tabPickerNote:
      'Επιλέξτε την καρτέλα της συνάντησης και ενεργοποιήστε την «Κοινή χρήση ήχου καρτέλας». Το πρόγραμμα περιήγησης το ονομάζει κοινή χρήση οθόνης, αλλά το Poneva καταγράφει μόνο ήχο και σταματά αμέσως το βίντεο. Επιλέξτε «Και τα δύο» για να συμπεριλάβετε το μικρόφωνό σας.',
    consentLine: 'Όλοι στη σύσκεψη γνωρίζουν ότι ηχογραφείται.',
    startRecording: 'Έναρξη ηχογράφησης',
    defaultTitle: 'Σύσκεψη {date}',
    stop: 'Διακοπή',
    stopRecordingLabel: 'Διακοπή ηχογράφησης ({time})',
    inputLevel: 'Στάθμη μικροφώνου',
    elapsed: 'Ηχογράφηση {time}',
    uploadBacklog: '{seconds} δ ήχου περιμένουν αποστολή.',
    saving: 'Αποθήκευση της ηχογράφησης… Απομένουν {seconds} δ ήχου για αποστολή.',
    savingOffline:
      'Εκτός σύνδεσης. Η ηχογράφηση παραμένει σε αυτή τη συσκευή και θα σταλεί όταν επανασυνδεθείτε.',
    sealing: 'Αποθήκευση της ηχογράφησης',
    recordingNow: 'Ηχογράφηση σε εξέλιξη',
    recordingElsewhere: 'Αυτή η σύσκεψη ηχογραφείται σε άλλη συσκευή.',
    openRecording: 'Άνοιγμα',
    discardRecording: 'Απόρριψη ηχογράφησης',
    resume: 'Συνέχιση ηχογράφησης',
    stopHere: 'Διακοπή και αποθήκευση',
    recoveryTitle: 'Μια ηχογράφηση διακόπηκε',
    recoveryBody:
      'Ο ήχος που ηχογραφήθηκε πριν από τη διακοπή είναι αποθηκευμένος. Συνεχίστε την ηχογράφηση ή σταματήστε και αποθηκεύστε την.',
    tabEnded:
      'Η καρτέλα της σύσκεψης σταμάτησε να κοινοποιείται. Η ηχογράφηση συνεχίζεται από το μικρόφωνο.',
    micEnded: 'Το μικρόφωνο σταμάτησε. Η ηχογράφηση συνεχίζεται από την καρτέλα της σύσκεψης.',
    micLost: 'Το μικρόφωνο σταμάτησε, οπότε η ηχογράφηση τέθηκε σε παύση.',
    limitWarning: 'Η ηχογράφηση σταματά σε {minutes} λεπτά.',
    limitReached: 'Η ηχογράφηση έφτασε στη μέγιστη διάρκεια και σταμάτησε.',
    previewCaption: 'Ζωντανή προεπισκόπηση. Η τελική απομαγνητοφώνηση γίνεται από την ηχογράφηση.',
    previewPaused: 'Η ζωντανή προεπισκόπηση είναι σε παύση.',
    previewLag: 'Το ζωντανό κείμενο καθυστερεί {seconds} δ.',
    previewNotShown: '{seconds} δ ήχου δεν εμφανίστηκαν ζωντανά. Έχουν ωστόσο ηχογραφηθεί.',
    previewUnavailable: 'Η ζωντανή προεπισκόπηση δεν είναι διαθέσιμη. Η ηχογράφηση συνεχίζεται.',
    micDenied: 'Επιτρέψτε το μικρόφωνο και δοκιμάστε ξανά.',
    noMic: 'Δεν βρέθηκε μικρόφωνο.',
    micBusy: 'Το μικρόφωνο χρησιμοποιείται από άλλη εφαρμογή.',
    insecureOrigin: 'Η ηχογράφηση απαιτεί ασφαλή (https) σύνδεση.',
    shareCancelled: 'Η κοινή χρήση της καρτέλας ακυρώθηκε.',
    shareNoAudio:
      'Επιλέξτε την καρτέλα της συνάντησης με ενεργή την «Κοινή χρήση ήχου καρτέλας». Ένα παράθυρο ή ολόκληρη η οθόνη δεν μπορεί να χρησιμοποιηθεί για ήχο καρτέλας.',
    tabUnsupported:
      'Αυτό το πρόγραμμα περιήγησης δεν μπορεί να καταγράψει ήχο καρτέλας συνάντησης. Χρησιμοποιήστε εδώ το μικρόφωνο, ανοίξτε τη συνάντηση και το Poneva σε Chrome ή Edge σε υπολογιστή ή εισαγάγετε μια εγγραφή.',
    storageLow:
      'Ο χώρος αποθήκευσης της συσκευής εξαντλείται. Ελευθερώστε χώρο πριν από την ηχογράφηση.',
    captureClosed: 'Αυτή η ηχογράφηση έχει ήδη σταματήσει.',
    captureActiveElsewhere: 'Ηχογραφείτε ήδη άλλη σύσκεψη.',
    recordUnavailable: 'Η ηχογράφηση δεν είναι διαθέσιμη αυτή τη στιγμή.',
    stopFailed:
      'Δεν ήταν δυνατή η διακοπή της ηχογράφησης. Ελέγξτε τη σύνδεση ή συνδεθείτε ξανά και δοκιμάστε πάλι.',
    signOutRecordingTitle: 'Η ηχογράφηση μιας σύσκεψης είναι ακόμη σε εξέλιξη',
    signOutRecordingBody:
      'Η αποσύνδεση τη σταματά. Ο ήχος που δεν έχει μεταφορτωθεί ακόμη μένει σε αυτή τη συσκευή και μεταφορτώνεται την επόμενη φορά που θα συνδεθείτε εδώ.',
    signOutAnyway: 'Αποσύνδεση',
    paymentRequired:
      'Η πίστωση χρήσης της εταιρείας σας εξαντλήθηκε. Ένας διαχειριστής μπορεί να προσθέσει κάρτα· στη συνέχεια δοκιμάστε ξανά.',
  },
  hu: {
    pageEyebrow: '{company} munkaterülete',
    pageTitle: 'Megbeszélések',
    pageDescription:
      'Rögzíts itt egy megbeszélést, vagy importálj felvételt vagy átiratot. A Poneva megőrzi az eredetit, és a céged kontextusa alapján forrásokkal ellátott jegyzeteket készít.',
    loadingMeetings: 'Megbeszélések betöltése…',
    loadFailed: 'Nem sikerült betölteni a megbeszéléseket.',
    emptyTitle: 'Minden megbeszélés használatra készen.',
    emptyDescription:
      'Rögzíts egy megbeszélést, vagy importálj felvételt vagy átiratot. Az eredeti elérhető marad, amíg a Poneva elkészíti az átiratot és a forrásokkal ellátott teendőket.',
    importMeeting: 'Megbeszélés importálása',
    importFirst: 'Első megbeszélés importálása',
    importUnavailable:
      'A hang importálása nem érhető el ezen a szerveren. Átiratokat továbbra is importálhatsz.',
    rowRecording: 'Felvétel… {time}',
    rowSaving: 'Mentés…',
    importDialogTitle: 'Megbeszélés importálása',
    importRetainedNote:
      'Az eredeti felvétel megmarad. A hangból kisebb beszédmásolat készül, amely nem helyettesíti az eredetit.',
    importSaved: 'A megbeszélés mentve. Az átírás a háttérben fut.',
    importFailed: 'Nem sikerült importálni ezt a megbeszélést.',
    uploadProgress: 'Feltöltés állapota',
    uploadingPercent: 'Feltöltés: {percent}%',
    titleLabel: 'Cím',
    membersLabel: 'Személyek innen: {company}',
    removeMember: '{name} eltávolítása',
    searchMembers: 'Keresés a cég tagjai között',
    you: '(te)',
    noMatchingMembers: 'Nincs egyező tag.',
    guestsLabel: 'Egyéb résztvevők',
    optional: '(nem kötelező)',
    guestsPlaceholder: 'Soronként egy név vagy e-mail-cím',
    cancel: 'Mégse',
    close: 'Bezárás',
    dismiss: 'Elvetés',
    retryStop: 'Újrapróbálás',
    record: 'Felvétel',
    starting: 'Indítás…',
    recordDialogTitle: 'Megbeszélés rögzítése',
    recordDialogNote:
      'A hang a felvétel közben a Chatbe mentődik. Az átirat és a jegyzetek a leállítás után a felvételből készülnek.',
    sourceLabel: 'Mit rögzítsünk',
    sourceRoom: 'Ez a helyiség',
    sourceRoomHint: 'Az eszköz mikrofonja.',
    sourceTab: 'Egy megbeszélés lapja',
    sourceTabHint: 'Egy hívás hangja egy másik böngészőlapon.',
    sourceBoth: 'Mindkettő',
    sourceBothHint: 'A hívás lapja és az eszköz mikrofonja.',
    tabPickerNote:
      'Válaszd ki a megbeszélés böngészőlapját, és kapcsold be a „Lap hangjának megosztása” lehetőséget. A böngésző ezt képernyőmegosztásnak nevezi, de a Poneva csak hangot rögzít, és azonnal leállítja a videót. A mikrofonodhoz válaszd a „Mindkettő” lehetőséget.',
    consentLine: 'A megbeszélés minden résztvevője tudja, hogy felvétel készül.',
    startRecording: 'Felvétel indítása',
    defaultTitle: 'Megbeszélés {date}',
    stop: 'Leállítás',
    stopRecordingLabel: 'Felvétel leállítása ({time})',
    inputLevel: 'Mikrofonszint',
    elapsed: 'Felvétel {time}',
    uploadBacklog: '{seconds} mp hang vár feltöltésre.',
    saving: 'A felvétel mentése… Még {seconds} mp hangot kell feltölteni.',
    savingOffline:
      'Nincs kapcsolat. A felvétel ezen az eszközön marad, és a kapcsolat helyreállásakor feltöltődik.',
    sealing: 'A felvétel mentése',
    recordingNow: 'Felvétel folyamatban',
    recordingElsewhere: 'Ezt a megbeszélést egy másik eszközön rögzítik.',
    openRecording: 'Megnyitás',
    discardRecording: 'Felvétel elvetése',
    resume: 'Felvétel folytatása',
    stopHere: 'Leállítás és mentés',
    recoveryTitle: 'Egy felvétel megszakadt',
    recoveryBody:
      'A megszakítás előtt rögzített hang mentve van. Folytasd a felvételt, vagy állítsd le és mentsd.',
    tabEnded: 'A megbeszélés lapjának megosztása megszűnt. A felvétel a mikrofonról folytatódik.',
    micEnded: 'A mikrofon leállt. A felvétel a megbeszélés lapjáról folytatódik.',
    micLost: 'A mikrofon leállt, ezért a felvétel szünetel.',
    limitWarning: 'A felvétel {minutes} perc múlva leáll.',
    limitReached: 'A felvétel elérte a maximális hosszt, és leállt.',
    previewCaption: 'Élő előnézet. A végleges átirat a felvételből készül.',
    previewPaused: 'Az élő előnézet szünetel.',
    previewLag: 'Az élő szöveg {seconds} mp-cel lemaradt.',
    previewNotShown: '{seconds} mp hang nem jelent meg élőben. Ettől még rögzítve van.',
    previewUnavailable: 'Az élő előnézet nem érhető el. A felvétel folytatódik.',
    micDenied: 'Engedélyezd a mikrofont, és próbáld újra.',
    noMic: 'Nem található mikrofon.',
    micBusy: 'A mikrofont egy másik alkalmazás használja.',
    insecureOrigin: 'A felvételhez biztonságos (https) kapcsolat szükséges.',
    shareCancelled: 'A lap megosztása megszakadt.',
    shareNoAudio:
      'Válaszd ki a megbeszélés lapját bekapcsolt hangmegosztással. Ablak vagy teljes képernyő nem használható a megbeszélés lapjának hangjához.',
    tabUnsupported:
      'Ez a böngésző nem tudja rögzíteni a megbeszélés lapjának hangját. Használd itt a mikrofont, nyisd meg a megbeszélést és a Ponevát asztali Chrome-ban vagy Edge-ben, vagy importálj felvételt.',
    storageLow: 'Az eszközön kevés a tárhely. Felvétel előtt szabadíts fel helyet.',
    captureClosed: 'Ezt a felvételt már leállították.',
    captureActiveElsewhere: 'Már rögzítesz egy másik megbeszélést.',
    recordUnavailable: 'A felvétel jelenleg nem érhető el.',
    stopFailed:
      'A felvételt nem sikerült leállítani. Ellenőrizd a kapcsolatot, vagy jelentkezz be újra, majd próbáld újra.',
    signOutRecordingTitle: 'Egy megbeszélés felvétele még folyamatban van',
    signOutRecordingBody:
      'A kijelentkezés leállítja. A még fel nem töltött hang ezen az eszközön marad, és a következő bejelentkezésedkor itt feltöltődik.',
    signOutAnyway: 'Kijelentkezés',
    paymentRequired:
      'A cég felhasználási kerete elfogyott. Egy adminisztrátor hozzáadhat egy kártyát, majd próbálja újra.',
  },
  ga: {
    pageEyebrow: 'Spás oibre {company}',
    pageTitle: 'Cruinnithe',
    pageDescription:
      'Taifead cruinniú anseo, nó iompórtáil taifeadadh nó tras-scríbhinn. Coinníonn Poneva an bunleagan agus ullmhaíonn sé nótaí le foinsí ag úsáid comhthéacs do chuideachta.',
    loadingMeetings: 'Cruinnithe á lódáil…',
    loadFailed: 'Níorbh fhéidir na cruinnithe a lódáil.',
    emptyTitle: 'Gach cruinniú, réidh le húsáid.',
    emptyDescription:
      'Taifead cruinniú, nó iompórtáil taifeadadh nó tras-scríbhinn. Fanann an bunleagan ar fáil fad a ullmhaíonn Poneva an tras-scríbhinn agus na chéad chéimeanna eile le foinsí.',
    importMeeting: 'Iompórtáil cruinniú',
    importFirst: 'Iompórtáil do chéad chruinniú',
    importUnavailable:
      'Níl iompórtáil fuaime ar fáil ar an bhfreastalaí seo. Is féidir leat tras-scríbhinní a iompórtáil fós.',
    rowRecording: 'Á thaifeadadh… {time}',
    rowSaving: 'Á shábháil…',
    importDialogTitle: 'Iompórtáil cruinniú',
    importRetainedNote:
      'Coinnítear an bunthaifeadadh. Próiseáiltear an fhuaim ina cóip cainte níos lú gan an bunleagan a athsholáthar.',
    importSaved: 'Sábháladh an cruinniú. Tá an tras-scríobh ar siúl sa chúlra.',
    importFailed: 'Níorbh fhéidir an cruinniú seo a iompórtáil.',
    uploadProgress: 'Dul chun cinn an uaslódála',
    uploadingPercent: 'Á uaslódáil {percent}%',
    titleLabel: 'Teideal',
    membersLabel: 'Daoine ó {company}',
    removeMember: 'Bain {name}',
    searchMembers: 'Cuardaigh baill na cuideachta',
    you: '(tusa)',
    noMatchingMembers: 'Níl aon bhall comhoiriúnach ann.',
    guestsLabel: 'Rannpháirtithe eile',
    optional: '(roghnach)',
    guestsPlaceholder: 'Ainm nó ríomhphost amháin ar gach líne',
    cancel: 'Cealaigh',
    close: 'Dún',
    dismiss: 'Ruaig',
    retryStop: 'Bain triail eile as',
    record: 'Taifead',
    starting: 'Ag tosú…',
    recordDialogTitle: 'Taifead cruinniú',
    recordDialogNote:
      'Sábháiltear an fhuaim in Chat agus tú ag taifeadadh. Déantar an tras-scríbhinn agus na nótaí ón taifeadadh nuair a stopann tú.',
    sourceLabel: 'Cad atá le taifeadadh',
    sourceRoom: 'An seomra seo',
    sourceRoomHint: 'Micreafón an ghléis seo.',
    sourceTab: 'Cluaisín cruinnithe',
    sourceTabHint: 'Fuaim ghlao i gcluaisín brabhsálaí eile.',
    sourceBoth: 'An dá cheann',
    sourceBothHint: 'Cluaisín an ghlao agus micreafón an ghléis seo.',
    tabPickerNote:
      'Roghnaigh cluaisín an chruinnithe agus cuir „Comhroinn fuaim an chluaisín“ ar siúl. Tugann an brabhsálaí comhroinnt scáileáin air seo, ach ní thaifeadann Poneva ach fuaim agus stopann sé an físeán láithreach. Roghnaigh „An dá cheann“ chun do mhicreafón a chur san áireamh.',
    consentLine: 'Tá a fhios ag gach duine sa chruinniú go bhfuil sé á thaifeadadh.',
    startRecording: 'Tosaigh ag taifeadadh',
    defaultTitle: 'Cruinniú {date}',
    stop: 'Stop',
    stopRecordingLabel: 'Stop an taifeadadh ({time})',
    inputLevel: 'Leibhéal an mhicreafóin',
    elapsed: 'Á thaifeadadh {time}',
    uploadBacklog: '{seconds} s fuaime ag fanacht le huaslódáil.',
    saving: 'An taifeadadh á shábháil… {seconds} s fuaime fós le huaslódáil.',
    savingOffline:
      'As líne. Fanann an taifeadadh ar an ngléas seo agus uaslódálfar é nuair a cheanglaíonn tú arís.',
    sealing: 'An taifeadadh á shábháil',
    recordingNow: 'Taifeadadh ar siúl',
    recordingElsewhere: 'Tá an cruinniú seo á thaifeadadh ar ghléas eile.',
    openRecording: 'Oscail',
    discardRecording: 'Scrios an taifeadadh',
    resume: 'Lean ar aghaidh ag taifeadadh',
    stopHere: 'Stop agus sábháil',
    recoveryTitle: 'Cuireadh isteach ar thaifeadadh',
    recoveryBody:
      'Tá an fhuaim a taifeadadh roimh an mbriseadh sábháilte. Lean ar aghaidh ag taifeadadh, nó stop agus sábháil é.',
    tabEnded:
      'Níl cluaisín an chruinnithe á chomhroinnt a thuilleadh. Leanann an taifeadadh ón micreafón.',
    micEnded: 'Stop an micreafón. Leanann an taifeadadh ó chluaisín an chruinnithe.',
    micLost: 'Stop an micreafón, mar sin cuireadh an taifeadadh ar sos.',
    limitWarning: 'Stopfaidh an taifeadadh i gceann {minutes} nóim.',
    limitReached: 'Shroich an taifeadadh a fhad uasta agus stopadh é.',
    previewCaption: 'Réamhamharc beo. Déantar an tras-scríbhinn deiridh ón taifeadadh.',
    previewPaused: 'Tá an réamhamharc beo ar sos.',
    previewLag: 'Tá an téacs beo {seconds} s ar gcúl.',
    previewNotShown: 'Níor taispeánadh {seconds} s fuaime beo. Tá sé taifeadta fós.',
    previewUnavailable: 'Níl an réamhamharc beo ar fáil. Leanann an taifeadadh ar aghaidh.',
    micDenied: 'Ceadaigh an micreafón agus bain triail eile as.',
    noMic: 'Níor aimsíodh aon mhicreafón.',
    micBusy: 'Tá aip eile ag úsáid an mhicreafóin.',
    insecureOrigin: 'Teastaíonn nasc slán (https) le taifeadadh.',
    shareCancelled: 'Cealaíodh comhroinnt an chluaisín.',
    shareNoAudio:
      'Roghnaigh cluaisín an chruinnithe le comhroinnt fuaime ar siúl. Ní féidir fuinneog ná an scáileán iomlán a úsáid le haghaidh fuaim chluaisín cruinnithe.',
    tabUnsupported:
      'Ní féidir leis an mbrabhsálaí seo fuaim chluaisín cruinnithe a ghabháil. Úsáid an micreafón anseo, oscail an cruinniú agus Poneva in Chrome nó Edge ar ríomhaire, nó iompórtáil taifeadadh.',
    storageLow: 'Tá an stóráil gann ar an ngléas seo. Saor spás sula dtaifeadann tú.',
    captureClosed: 'Stopadh an taifeadadh seo cheana.',
    captureActiveElsewhere: 'Tá cruinniú eile á thaifeadadh agat cheana.',
    recordUnavailable: 'Níl taifeadadh ar fáil faoi láthair.',
    stopFailed:
      'Níorbh fhéidir an taifeadadh a stopadh. Seiceáil do nasc nó sínigh isteach arís, ansin bain triail eile as.',
    signOutRecordingTitle: 'Tá taifeadadh cruinnithe fós ar siúl',
    signOutRecordingBody:
      'Stopann sé nuair a shíníonn tú amach. Fanann fuaim nach bhfuil uaslódáilte fós ar an ngléas seo agus uaslódálfar í an chéad uair eile a shíníonn tú isteach anseo.',
    signOutAnyway: 'Sínigh amach',
    paymentRequired:
      'Tá creidmheas úsáide do chuideachta ídithe. Is féidir le riarthóir cárta a chur leis, agus ansin bain triail eile as.',
  },
  it: {
    pageEyebrow: 'Area di lavoro di {company}',
    pageTitle: 'Riunioni',
    pageDescription:
      'Registra qui una riunione, oppure importa una registrazione o una trascrizione. Poneva conserva l’originale e prepara note con le fonti usando il contesto della tua azienda.',
    loadingMeetings: 'Caricamento delle riunioni…',
    loadFailed: 'Impossibile caricare le riunioni.',
    emptyTitle: 'Ogni riunione, pronta all’uso.',
    emptyDescription:
      'Registra una riunione, oppure importa una registrazione o una trascrizione. L’originale resta disponibile mentre Poneva prepara la trascrizione e le azioni successive con le fonti.',
    importMeeting: 'Importa riunione',
    importFirst: 'Importa la tua prima riunione',
    importUnavailable:
      'L’importazione dell’audio non è disponibile su questo server. Puoi comunque importare trascrizioni.',
    rowRecording: 'Registrazione… {time}',
    rowSaving: 'Salvataggio…',
    importDialogTitle: 'Importa una riunione',
    importRetainedNote:
      'La registrazione originale viene conservata. L’audio viene elaborato in una copia vocale più leggera senza sostituire l’originale.',
    importSaved: 'Riunione salvata. La trascrizione è in corso in background.',
    importFailed: 'Impossibile importare questa riunione.',
    uploadProgress: 'Avanzamento del caricamento',
    uploadingPercent: 'Caricamento {percent}%',
    titleLabel: 'Titolo',
    membersLabel: 'Persone di {company}',
    removeMember: 'Rimuovi {name}',
    searchMembers: 'Cerca membri dell’azienda',
    you: '(tu)',
    noMatchingMembers: 'Nessun membro corrispondente.',
    guestsLabel: 'Altri partecipanti',
    optional: '(facoltativo)',
    guestsPlaceholder: 'Un nome o un indirizzo email per riga',
    cancel: 'Annulla',
    close: 'Chiudi',
    dismiss: 'Ignora',
    retryStop: 'Riprova',
    record: 'Registra',
    starting: 'Avvio…',
    recordDialogTitle: 'Registra una riunione',
    recordDialogNote:
      'L’audio viene salvato in Chat durante la registrazione. La trascrizione e le note vengono create dalla registrazione dopo l’interruzione.',
    sourceLabel: 'Cosa registrare',
    sourceRoom: 'Questa stanza',
    sourceRoomHint: 'Il microfono di questo dispositivo.',
    sourceTab: 'Una scheda della riunione',
    sourceTabHint: 'L’audio di una chiamata in un’altra scheda del browser.',
    sourceBoth: 'Entrambi',
    sourceBothHint: 'La scheda della chiamata e il microfono di questo dispositivo.',
    tabPickerNote:
      'Scegli la scheda della riunione e attiva “Condividi audio della scheda”. Il browser la chiama condivisione dello schermo, ma Poneva registra solo l’audio e interrompe subito il video. Scegli “Entrambi” per includere il microfono.',
    consentLine: 'Tutti i partecipanti sanno che la riunione viene registrata.',
    startRecording: 'Avvia la registrazione',
    defaultTitle: 'Riunione {date}',
    stop: 'Interrompi',
    stopRecordingLabel: 'Interrompi la registrazione ({time})',
    inputLevel: 'Livello del microfono',
    elapsed: 'Registrazione {time}',
    uploadBacklog: '{seconds} s di audio in attesa di caricamento.',
    saving: 'Salvataggio della registrazione… Restano {seconds} s di audio da caricare.',
    savingOffline:
      'Offline. La registrazione resta su questo dispositivo e verrà caricata quando tornerai online.',
    sealing: 'Salvataggio della registrazione',
    recordingNow: 'Registrazione in corso',
    recordingElsewhere: 'Questa riunione è in registrazione su un altro dispositivo.',
    openRecording: 'Apri',
    discardRecording: 'Elimina la registrazione',
    resume: 'Riprendi la registrazione',
    stopHere: 'Interrompi e salva',
    recoveryTitle: 'Una registrazione è stata interrotta',
    recoveryBody:
      'L’audio registrato prima dell’interruzione è salvato. Riprendi la registrazione, oppure interrompi e salvala.',
    tabEnded:
      'La scheda della riunione non è più condivisa. La registrazione continua dal microfono.',
    micEnded:
      'Il microfono si è interrotto. La registrazione continua dalla scheda della riunione.',
    micLost: 'Il microfono si è interrotto, quindi la registrazione è stata messa in pausa.',
    limitWarning: 'La registrazione si interrompe tra {minutes} min.',
    limitReached: 'La registrazione ha raggiunto la durata massima ed è stata interrotta.',
    previewCaption: 'Anteprima dal vivo. La trascrizione finale viene creata dalla registrazione.',
    previewPaused: 'Anteprima dal vivo in pausa.',
    previewLag: 'Il testo dal vivo è indietro di {seconds} s.',
    previewNotShown:
      '{seconds} s di audio non sono stati mostrati dal vivo. Sono comunque registrati.',
    previewUnavailable: 'L’anteprima dal vivo non è disponibile. La registrazione continua.',
    micDenied: 'Consenti l’accesso al microfono e riprova.',
    noMic: 'Nessun microfono trovato.',
    micBusy: 'Il microfono è in uso da un’altra app.',
    insecureOrigin: 'Per registrare serve una connessione sicura (https).',
    shareCancelled: 'La condivisione della scheda è stata annullata.',
    shareNoAudio:
      'Scegli la scheda della riunione con “Condividi audio della scheda” attivo. Una finestra o l’intero schermo non possono essere usati per l’audio di una scheda riunione.',
    tabUnsupported:
      'Questo browser non può acquisire l’audio di una scheda riunione. Usa il microfono qui, apri la riunione e Poneva in Chrome o Edge su computer oppure importa una registrazione.',
    storageLow:
      'Lo spazio di archiviazione del dispositivo è quasi esaurito. Libera spazio prima di registrare.',
    captureClosed: 'Questa registrazione è già stata interrotta.',
    captureActiveElsewhere: 'Stai già registrando un’altra riunione.',
    recordUnavailable: 'La registrazione non è disponibile al momento.',
    stopFailed:
      'Non è stato possibile interrompere la registrazione. Controlla la connessione o accedi di nuovo, poi riprova.',
    signOutRecordingTitle: 'È ancora in corso la registrazione di una riunione',
    signOutRecordingBody:
      'Uscendo si interrompe. L’audio non ancora caricato resta su questo dispositivo e viene caricato al prossimo accesso da qui.',
    signOutAnyway: 'Esci',
    paymentRequired:
      'Il credito di utilizzo della tua azienda è esaurito. Un amministratore può aggiungere una carta, poi riprova.',
  },
  lv: {
    pageEyebrow: '{company} darbvieta',
    pageTitle: 'Sapulces',
    pageDescription:
      'Ierakstiet sapulci šeit vai importējiet ierakstu vai atšifrējumu. Poneva saglabā oriģinālu un sagatavo piezīmes ar avotiem, izmantojot jūsu uzņēmuma kontekstu.',
    loadingMeetings: 'Ielādē sapulces…',
    loadFailed: 'Neizdevās ielādēt sapulces.',
    emptyTitle: 'Katra sapulce, gatava lietošanai.',
    emptyDescription:
      'Ierakstiet sapulci vai importējiet ierakstu vai atšifrējumu. Oriģināls paliek pieejams, kamēr Poneva sagatavo atšifrējumu un turpmākās darbības ar avotiem.',
    importMeeting: 'Importēt sapulci',
    importFirst: 'Importējiet savu pirmo sapulci',
    importUnavailable:
      'Audio importēšana šajā serverī nav pieejama. Atšifrējumus joprojām varat importēt.',
    rowRecording: 'Notiek ierakstīšana… {time}',
    rowSaving: 'Saglabā…',
    importDialogTitle: 'Sapulces importēšana',
    importRetainedNote:
      'Oriģinālais ieraksts tiek saglabāts. Audio tiek apstrādāts mazākā runas kopijā, neaizstājot oriģinālu.',
    importSaved: 'Sapulce saglabāta. Atšifrēšana notiek fonā.',
    importFailed: 'Neizdevās importēt šo sapulci.',
    uploadProgress: 'Augšupielādes progress',
    uploadingPercent: 'Augšupielādē {percent}%',
    titleLabel: 'Nosaukums',
    membersLabel: 'Personas no {company}',
    removeMember: 'Noņemt: {name}',
    searchMembers: 'Meklēt uzņēmuma dalībniekus',
    you: '(jūs)',
    noMatchingMembers: 'Nav atbilstošu dalībnieku.',
    guestsLabel: 'Citi dalībnieki',
    optional: '(neobligāti)',
    guestsPlaceholder: 'Viens vārds vai e-pasta adrese katrā rindā',
    cancel: 'Atcelt',
    close: 'Aizvērt',
    dismiss: 'Nerādīt',
    retryStop: 'Mēģināt vēlreiz',
    record: 'Ierakstīt',
    starting: 'Palaiž…',
    recordDialogTitle: 'Ierakstīt sapulci',
    recordDialogNote:
      'Audio ierakstīšanas laikā tiek saglabāts Chat. Atšifrējums un piezīmes tiek sagatavotas no ieraksta pēc apturēšanas.',
    sourceLabel: 'Ko ierakstīt',
    sourceRoom: 'Šī telpa',
    sourceRoomHint: 'Šīs ierīces mikrofons.',
    sourceTab: 'Sapulces cilne',
    sourceTabHint: 'Zvana skaņa citā pārlūka cilnē.',
    sourceBoth: 'Abi',
    sourceBothHint: 'Zvana cilne un šīs ierīces mikrofons.',
    tabPickerNote:
      'Izvēlieties sapulces pārlūka cilni un ieslēdziet „Kopīgot cilnes audio“. Pārlūks to sauc par ekrāna kopīgošanu, bet Poneva ieraksta tikai audio un nekavējoties aptur video. Izvēlieties „Abi“, lai iekļautu mikrofonu.',
    consentLine: 'Visi sapulces dalībnieki zina, ka tā tiek ierakstīta.',
    startRecording: 'Sākt ierakstīšanu',
    defaultTitle: 'Sapulce {date}',
    stop: 'Apturēt',
    stopRecordingLabel: 'Apturēt ierakstīšanu ({time})',
    inputLevel: 'Mikrofona līmenis',
    elapsed: 'Ierakstīšana {time}',
    uploadBacklog: '{seconds} s audio gaida augšupielādi.',
    saving: 'Saglabā ierakstu… Vēl jāaugšupielādē {seconds} s audio.',
    savingOffline:
      'Bezsaistē. Ieraksts paliek šajā ierīcē un tiks augšupielādēts, kad atjaunosies savienojums.',
    sealing: 'Saglabā ierakstu',
    recordingNow: 'Notiek ierakstīšana',
    recordingElsewhere: 'Šī sapulce tiek ierakstīta citā ierīcē.',
    openRecording: 'Atvērt',
    discardRecording: 'Atmest ierakstu',
    resume: 'Turpināt ierakstīšanu',
    stopHere: 'Apturēt un saglabāt',
    recoveryTitle: 'Ierakstīšana tika pārtraukta',
    recoveryBody:
      'Pirms pārtraukuma ierakstītais audio ir saglabāts. Turpiniet ierakstīšanu vai apturiet un saglabājiet to.',
    tabEnded: 'Sapulces cilne vairs netiek kopīgota. Ierakstīšana turpinās no mikrofona.',
    micEnded: 'Mikrofons pārstāja darboties. Ierakstīšana turpinās no sapulces cilnes.',
    micLost: 'Mikrofons pārstāja darboties, tāpēc ierakstīšana tika pauzēta.',
    limitWarning: 'Ierakstīšana beigsies pēc {minutes} min.',
    limitReached: 'Ieraksts sasniedza maksimālo garumu un tika apturēts.',
    previewCaption: 'Tiešraides priekšskatījums. Galīgais atšifrējums tiek veidots no ieraksta.',
    previewPaused: 'Tiešraides priekšskatījums ir pauzēts.',
    previewLag: 'Tiešraides teksts atpaliek par {seconds} s.',
    previewNotShown: '{seconds} s audio netika parādītas tiešraidē. Tās tomēr ir ierakstītas.',
    previewUnavailable: 'Tiešraides priekšskatījums nav pieejams. Ierakstīšana turpinās.',
    micDenied: 'Atļaujiet mikrofonu un mēģiniet vēlreiz.',
    noMic: 'Mikrofons netika atrasts.',
    micBusy: 'Mikrofonu izmanto cita lietotne.',
    insecureOrigin: 'Ierakstīšanai nepieciešams drošs (https) savienojums.',
    shareCancelled: 'Cilnes kopīgošana tika atcelta.',
    shareNoAudio:
      'Izvēlieties sapulces cilni ar ieslēgtu „Kopīgot cilnes audio“. Logu vai visu ekrānu nevar izmantot sapulces cilnes audio.',
    tabUnsupported:
      'Šis pārlūks nevar ierakstīt sapulces cilnes audio. Izmantojiet šeit mikrofonu, atveriet sapulci un Poneva datora pārlūkā Chrome vai Edge vai importējiet ierakstu.',
    storageLow: 'Ierīcē beidzas vieta. Pirms ierakstīšanas atbrīvojiet vietu.',
    captureClosed: 'Šī ierakstīšana jau ir apturēta.',
    captureActiveElsewhere: 'Jūs jau ierakstāt citu sapulci.',
    recordUnavailable: 'Ierakstīšana pašlaik nav pieejama.',
    stopFailed:
      'Ierakstu neizdevās apturēt. Pārbaudiet savienojumu vai pierakstieties vēlreiz un mēģiniet vēlreiz.',
    signOutRecordingTitle: 'Sanāksmes ierakstīšana vēl notiek',
    signOutRecordingBody:
      'Izrakstīšanās to aptur. Vēl neaugšupielādētais audio paliek šajā ierīcē un tiks augšupielādēts, kad nākamreiz šeit pierakstīsieties.',
    signOutAnyway: 'Izrakstīties',
    paymentRequired:
      'Jūsu uzņēmuma lietošanas kredīts ir izlietots. Administrators var pievienot karti, pēc tam mēģiniet vēlreiz.',
  },
  lt: {
    pageEyebrow: '{company} darbo sritis',
    pageTitle: 'Susitikimai',
    pageDescription:
      'Įrašykite susitikimą čia arba importuokite įrašą ar nuorašą. Poneva išsaugo originalą ir, remdamasi jūsų įmonės kontekstu, parengia pastabas su šaltiniais.',
    loadingMeetings: 'Įkeliami susitikimai…',
    loadFailed: 'Nepavyko įkelti susitikimų.',
    emptyTitle: 'Kiekvienas susitikimas – paruoštas naudoti.',
    emptyDescription:
      'Įrašykite susitikimą arba importuokite įrašą ar nuorašą. Originalas lieka pasiekiamas, kol Poneva rengia nuorašą ir tolesnius veiksmus su šaltiniais.',
    importMeeting: 'Importuoti susitikimą',
    importFirst: 'Importuokite pirmąjį susitikimą',
    importUnavailable:
      'Garso importavimas šiame serveryje nepasiekiamas. Nuorašus vis tiek galite importuoti.',
    rowRecording: 'Įrašoma… {time}',
    rowSaving: 'Išsaugoma…',
    importDialogTitle: 'Susitikimo importavimas',
    importRetainedNote:
      'Originalus įrašas išsaugomas. Garsas apdorojamas į mažesnę kalbos kopiją, nepakeičiant originalo.',
    importSaved: 'Susitikimas išsaugotas. Transkribavimas vyksta fone.',
    importFailed: 'Nepavyko importuoti šio susitikimo.',
    uploadProgress: 'Įkėlimo eiga',
    uploadingPercent: 'Įkeliama {percent} %',
    titleLabel: 'Pavadinimas',
    membersLabel: 'Žmonės iš {company}',
    removeMember: 'Pašalinti: {name}',
    searchMembers: 'Ieškoti įmonės narių',
    you: '(jūs)',
    noMatchingMembers: 'Atitinkančių narių nėra.',
    guestsLabel: 'Kiti dalyviai',
    optional: '(neprivaloma)',
    guestsPlaceholder: 'Po vieną vardą arba el. pašto adresą eilutėje',
    cancel: 'Atšaukti',
    close: 'Uždaryti',
    dismiss: 'Atmesti',
    retryStop: 'Bandyti dar kartą',
    record: 'Įrašyti',
    starting: 'Paleidžiama…',
    recordDialogTitle: 'Įrašyti susitikimą',
    recordDialogNote:
      'Įrašant garsas išsaugomas Chat. Nuorašas ir pastabos parengiami iš įrašo, kai sustabdote.',
    sourceLabel: 'Ką įrašyti',
    sourceRoom: 'Šis kambarys',
    sourceRoomHint: 'Šio įrenginio mikrofonas.',
    sourceTab: 'Susitikimo skirtukas',
    sourceTabHint: 'Skambučio garsas kitame naršyklės skirtuke.',
    sourceBoth: 'Abu',
    sourceBothHint: 'Skambučio skirtukas ir šio įrenginio mikrofonas.',
    tabPickerNote:
      'Pasirinkite susitikimo naršyklės kortelę ir įjunkite „Bendrinti kortelės garsą“. Naršyklė tai vadina ekrano bendrinimu, bet Poneva įrašo tik garsą ir iškart sustabdo vaizdą. Pasirinkite „Abu“, kad įtrauktumėte mikrofoną.',
    consentLine: 'Visi susitikimo dalyviai žino, kad jis įrašomas.',
    startRecording: 'Pradėti įrašymą',
    defaultTitle: 'Susitikimas {date}',
    stop: 'Sustabdyti',
    stopRecordingLabel: 'Sustabdyti įrašymą ({time})',
    inputLevel: 'Mikrofono lygis',
    elapsed: 'Įrašoma {time}',
    uploadBacklog: '{seconds} s garso laukia įkėlimo.',
    saving: 'Išsaugomas įrašas… Dar liko įkelti {seconds} s garso.',
    savingOffline:
      'Neprisijungta. Įrašas lieka šiame įrenginyje ir bus įkeltas, kai vėl prisijungsite.',
    sealing: 'Išsaugomas įrašas',
    recordingNow: 'Vyksta įrašymas',
    recordingElsewhere: 'Šis susitikimas įrašomas kitame įrenginyje.',
    openRecording: 'Atidaryti',
    discardRecording: 'Atmesti įrašą',
    resume: 'Tęsti įrašymą',
    stopHere: 'Sustabdyti ir išsaugoti',
    recoveryTitle: 'Įrašymas buvo nutrauktas',
    recoveryBody:
      'Iki pertraukos įrašytas garsas išsaugotas. Tęskite įrašymą arba sustabdykite ir išsaugokite jį.',
    tabEnded: 'Susitikimo skirtukas nebėra bendrinamas. Įrašymas tęsiamas iš mikrofono.',
    micEnded: 'Mikrofonas nustojo veikti. Įrašymas tęsiamas iš susitikimo skirtuko.',
    micLost: 'Mikrofonas nustojo veikti, todėl įrašymas pristabdytas.',
    limitWarning: 'Įrašymas sustos po {minutes} min.',
    limitReached: 'Įrašas pasiekė didžiausią trukmę ir buvo sustabdytas.',
    previewCaption: 'Tiesioginė peržiūra. Galutinis nuorašas parengiamas iš įrašo.',
    previewPaused: 'Tiesioginė peržiūra pristabdyta.',
    previewLag: 'Tiesioginis tekstas vėluoja {seconds} s.',
    previewNotShown: '{seconds} s garso nebuvo parodyta tiesiogiai. Jis vis tiek įrašytas.',
    previewUnavailable: 'Tiesioginė peržiūra nepasiekiama. Įrašymas tęsiamas.',
    micDenied: 'Leiskite naudoti mikrofoną ir bandykite dar kartą.',
    noMic: 'Mikrofonas nerastas.',
    micBusy: 'Mikrofoną naudoja kita programa.',
    insecureOrigin: 'Įrašymui reikia saugaus (https) ryšio.',
    shareCancelled: 'Skirtuko bendrinimas atšauktas.',
    shareNoAudio:
      'Pasirinkite susitikimo kortelę su įjungtu „Bendrinti kortelės garsą“. Lango ar viso ekrano negalima naudoti susitikimo kortelės garsui.',
    tabUnsupported:
      'Ši naršyklė negali įrašyti susitikimo kortelės garso. Čia naudokite mikrofoną, atidarykite susitikimą ir Poneva kompiuterio Chrome arba Edge naršyklėje arba importuokite įrašą.',
    storageLow: 'Įrenginyje baigiasi vieta. Prieš įrašydami atlaisvinkite vietos.',
    captureClosed: 'Šis įrašymas jau sustabdytas.',
    captureActiveElsewhere: 'Jau įrašote kitą susitikimą.',
    recordUnavailable: 'Įrašymas šiuo metu nepasiekiamas.',
    stopFailed:
      'Įrašymo nepavyko sustabdyti. Patikrinkite ryšį arba prisijunkite iš naujo ir bandykite dar kartą.',
    signOutRecordingTitle: 'Susitikimo įrašymas dar vyksta',
    signOutRecordingBody:
      'Atsijungus jis sustabdomas. Dar neįkeltas garsas lieka šiame įrenginyje ir bus įkeltas, kai kitą kartą čia prisijungsite.',
    signOutAnyway: 'Atsijungti',
    paymentRequired:
      'Jūsų įmonės naudojimo kreditas išnaudotas. Administratorius gali pridėti kortelę, tada bandykite dar kartą.',
  },
  mt: {
    pageEyebrow: 'Spazju tax-xogħol ta’ {company}',
    pageTitle: 'Laqgħat',
    pageDescription:
      'Irrekordja laqgħa hawn, jew importa reġistrazzjoni jew traskrizzjoni. Poneva żżomm l-oriġinal u tħejji noti b’sorsi billi tuża l-kuntest tal-kumpanija tiegħek.',
    loadingMeetings: 'Il-laqgħat qed jitgħabbew…',
    loadFailed: 'Ma stajniex intellgħu l-laqgħat.',
    emptyTitle: 'Kull laqgħa, lesta biex tintuża.',
    emptyDescription:
      'Irrekordja laqgħa, jew importa reġistrazzjoni jew traskrizzjoni. L-oriġinal jibqa’ disponibbli waqt li Poneva tħejji t-traskrizzjoni u l-passi li jmiss b’sorsi.',
    importMeeting: 'Importa laqgħa',
    importFirst: 'Importa l-ewwel laqgħa tiegħek',
    importUnavailable:
      'L-importazzjoni tal-awdjo mhijiex disponibbli fuq dan is-server. Xorta tista’ timporta traskrizzjonijiet.',
    rowRecording: 'Qed jirrekordja… {time}',
    rowSaving: 'Qed jissejvja…',
    importDialogTitle: 'Importa laqgħa',
    importRetainedNote:
      'Ir-reġistrazzjoni oriġinali tinżamm. L-awdjo jiġi pproċessat f’kopja iżgħar tad-diskors mingħajr ma tissostitwixxi l-oriġinal.',
    importSaved: 'Il-laqgħa ġiet issejvjata. It-traskrizzjoni qed issir fl-isfond.',
    importFailed: 'Ma stajniex nimportaw din il-laqgħa.',
    uploadProgress: 'Progress tat-tlugħ',
    uploadingPercent: 'Qed jitla’ {percent}%',
    titleLabel: 'Titlu',
    membersLabel: 'Nies minn {company}',
    removeMember: 'Neħħi lil {name}',
    searchMembers: 'Fittex membri tal-kumpanija',
    you: '(int)',
    noMatchingMembers: 'L-ebda membru ma jaqbel.',
    guestsLabel: 'Parteċipanti oħra',
    optional: '(mhux obbligatorju)',
    guestsPlaceholder: 'Isem jew email wieħed f’kull linja',
    cancel: 'Ikkanċella',
    close: 'Agħlaq',
    dismiss: 'Warrab',
    retryStop: 'Erġa’ pprova',
    record: 'Irrekordja',
    starting: 'Qed jibda…',
    recordDialogTitle: 'Irrekordja laqgħa',
    recordDialogNote:
      'L-awdjo jiġi ssejvjat f’Chat waqt li tirrekordja. It-traskrizzjoni u n-noti jsiru mir-reġistrazzjoni wara li tieqaf.',
    sourceLabel: 'X’għandu jiġi rrekordjat',
    sourceRoom: 'Din il-kamra',
    sourceRoomHint: 'Il-mikrofonu ta’ dan l-apparat.',
    sourceTab: 'Tab ta’ laqgħa',
    sourceTabHint: 'Il-ħoss ta’ sejħa f’tab ieħor tal-browser.',
    sourceBoth: 'It-tnejn',
    sourceBothHint: 'It-tab tas-sejħa u l-mikrofonu ta’ dan l-apparat.',
    tabPickerNote:
      'Agħżel it-tab tal-laqgħa u ixgħel “Aqsam l-awdjo tat-tab”. Il-browser isejjaħ dan qsim tal-iskrin, iżda Poneva jirrekordja biss l-awdjo u jwaqqaf il-vidjo minnufih. Agħżel “It-tnejn” biex tinkludi l-mikrofonu tiegħek.',
    consentLine: 'Kulħadd fil-laqgħa jaf li qed tiġi rrekordjata.',
    startRecording: 'Ibda r-reġistrazzjoni',
    defaultTitle: 'Laqgħa {date}',
    stop: 'Waqqaf',
    stopRecordingLabel: 'Waqqaf ir-reġistrazzjoni ({time})',
    inputLevel: 'Livell tal-mikrofonu',
    elapsed: 'Qed jirrekordja {time}',
    uploadBacklog: '{seconds} s ta’ awdjo qed jistennew biex jitilgħu.',
    saving: 'Ir-reġistrazzjoni qed tiġi ssejvjata… Għad fadal {seconds} s ta’ awdjo biex jitilgħu.',
    savingOffline:
      'Offline. Ir-reġistrazzjoni tinżamm fuq dan l-apparat u titla’ meta terġa’ tikkonnettja.',
    sealing: 'Ir-reġistrazzjoni qed tiġi ssejvjata',
    recordingNow: 'Ir-reġistrazzjoni għaddejja',
    recordingElsewhere: 'Din il-laqgħa qed tiġi rrekordjata fuq apparat ieħor.',
    openRecording: 'Iftaħ',
    discardRecording: 'Armi r-reġistrazzjoni',
    resume: 'Kompli r-reġistrazzjoni',
    stopHere: 'Waqqaf u ssejvja',
    recoveryTitle: 'Reġistrazzjoni ġiet interrotta',
    recoveryBody:
      'L-awdjo rrekordjat qabel l-interruzzjoni huwa ssejvjat. Kompli rrekordja, jew waqqafha u ssejvjaha.',
    tabEnded: 'It-tab tal-laqgħa ma baqax jinqasam. Ir-reġistrazzjoni tkompli mill-mikrofonu.',
    micEnded: 'Il-mikrofonu waqaf. Ir-reġistrazzjoni tkompli mit-tab tal-laqgħa.',
    micLost: 'Il-mikrofonu waqaf, għalhekk ir-reġistrazzjoni twaqqfet għal ftit.',
    limitWarning: 'Ir-reġistrazzjoni tieqaf f’{minutes} min.',
    limitReached: 'Ir-reġistrazzjoni laħqet it-tul massimu tagħha u twaqqfet.',
    previewCaption: 'Previżjoni diretta. It-traskrizzjoni finali ssir mir-reġistrazzjoni.',
    previewPaused: 'Il-previżjoni diretta hija waqfa.',
    previewLag: 'It-test dirett huwa {seconds} s lura.',
    previewNotShown: '{seconds} s ta’ awdjo ma ntwerewx dirett. Xorta ġew irrekordjati.',
    previewUnavailable: 'Il-previżjoni diretta mhijiex disponibbli. Ir-reġistrazzjoni tkompli.',
    micDenied: 'Ippermetti l-mikrofonu u erġa’ pprova.',
    noMic: 'Ma nstab l-ebda mikrofonu.',
    micBusy: 'Il-mikrofonu qed jintuża minn app oħra.',
    insecureOrigin: 'Ir-reġistrazzjoni teħtieġ konnessjoni sigura (https).',
    shareCancelled: 'Il-qsim tat-tab ġie kkanċellat.',
    shareNoAudio:
      'Agħżel it-tab tal-laqgħa b’“Aqsam l-awdjo tat-tab” mixgħul. Tieqa jew l-iskrin kollu ma jistgħux jintużaw għall-awdjo tat-tab tal-laqgħa.',
    tabUnsupported:
      'Dan il-browser ma jistax jaqbad l-awdjo tat-tab tal-laqgħa. Uża l-mikrofonu hawn, iftaħ il-laqgħa u Poneva f’Chrome jew Edge fuq kompjuter, jew importa reġistrazzjoni.',
    storageLow: 'Dan l-apparat għandu ftit spazju. Eħles spazju qabel ma tirrekordja.',
    captureClosed: 'Din ir-reġistrazzjoni diġà twaqqfet.',
    captureActiveElsewhere: 'Diġà qed tirrekordja laqgħa oħra.',
    recordUnavailable: 'Ir-reġistrazzjoni mhijiex disponibbli bħalissa.',
    stopFailed:
      'Ir-reġistrazzjoni ma setgħetx titwaqqaf. Iċċekkja l-konnessjoni jew erġa’ idħol, imbagħad erġa’ pprova.',
    signOutRecordingTitle: 'Għadha għaddejja reġistrazzjoni ta’ laqgħa',
    signOutRecordingBody:
      'Meta toħroġ, tieqaf. L-awdjo li għadu ma ttellax jibqa’ fuq dan l-apparat u jitlesta t-tlugħ id-darba li jmiss li tidħol hawn.',
    signOutAnyway: 'Oħroġ',
    paymentRequired:
      'Il-kreditu tal-użu tal-kumpanija tiegħek spiċċa. Amministratur jista’ jżid karta, imbagħad erġa’ pprova.',
  },
  pl: {
    pageEyebrow: 'Obszar roboczy {company}',
    pageTitle: 'Spotkania',
    pageDescription:
      'Nagraj tutaj spotkanie albo zaimportuj nagranie lub transkrypcję. Poneva zachowuje oryginał i przygotowuje notatki ze źródłami na podstawie kontekstu Twojej firmy.',
    loadingMeetings: 'Wczytywanie spotkań…',
    loadFailed: 'Nie udało się wczytać spotkań.',
    emptyTitle: 'Każde spotkanie gotowe do użycia.',
    emptyDescription:
      'Nagraj spotkanie albo zaimportuj nagranie lub transkrypcję. Oryginał pozostaje dostępny, a Poneva przygotowuje transkrypcję i dalsze kroki ze źródłami.',
    importMeeting: 'Importuj spotkanie',
    importFirst: 'Zaimportuj pierwsze spotkanie',
    importUnavailable:
      'Import dźwięku nie jest dostępny na tym serwerze. Nadal możesz importować transkrypcje.',
    rowRecording: 'Nagrywanie… {time}',
    rowSaving: 'Zapisywanie…',
    importDialogTitle: 'Importuj spotkanie',
    importRetainedNote:
      'Oryginalne nagranie zostaje zachowane. Dźwięk jest przetwarzany na mniejszą kopię mowy bez zastępowania oryginału.',
    importSaved: 'Spotkanie zapisane. Transkrypcja trwa w tle.',
    importFailed: 'Nie udało się zaimportować tego spotkania.',
    uploadProgress: 'Postęp przesyłania',
    uploadingPercent: 'Przesyłanie {percent}%',
    titleLabel: 'Tytuł',
    membersLabel: 'Osoby z {company}',
    removeMember: 'Usuń: {name}',
    searchMembers: 'Szukaj członków firmy',
    you: '(Ty)',
    noMatchingMembers: 'Brak pasujących członków.',
    guestsLabel: 'Inni uczestnicy',
    optional: '(opcjonalnie)',
    guestsPlaceholder: 'Jedno imię i nazwisko lub adres e-mail w wierszu',
    cancel: 'Anuluj',
    close: 'Zamknij',
    dismiss: 'Odrzuć',
    retryStop: 'Spróbuj ponownie',
    record: 'Nagraj',
    starting: 'Uruchamianie…',
    recordDialogTitle: 'Nagraj spotkanie',
    recordDialogNote:
      'Dźwięk jest zapisywany w Chat podczas nagrywania. Transkrypcja i notatki powstają z nagrania po jego zatrzymaniu.',
    sourceLabel: 'Co nagrać',
    sourceRoom: 'To pomieszczenie',
    sourceRoomHint: 'Mikrofon tego urządzenia.',
    sourceTab: 'Karta spotkania',
    sourceTabHint: 'Dźwięk rozmowy w innej karcie przeglądarki.',
    sourceBoth: 'Oba',
    sourceBothHint: 'Karta rozmowy i mikrofon tego urządzenia.',
    tabPickerNote:
      'Wybierz kartę spotkania i włącz „Udostępnij dźwięk karty”. Przeglądarka nazywa to udostępnianiem ekranu, ale Poneva nagrywa tylko dźwięk i od razu zatrzymuje wideo. Wybierz „Oba”, aby uwzględnić mikrofon.',
    consentLine: 'Wszyscy uczestnicy wiedzą, że spotkanie jest nagrywane.',
    startRecording: 'Rozpocznij nagrywanie',
    defaultTitle: 'Spotkanie {date}',
    stop: 'Zatrzymaj',
    stopRecordingLabel: 'Zatrzymaj nagrywanie ({time})',
    inputLevel: 'Poziom mikrofonu',
    elapsed: 'Nagrywanie {time}',
    uploadBacklog: '{seconds} s dźwięku czeka na przesłanie.',
    saving: 'Zapisywanie nagrania… Pozostało {seconds} s dźwięku do przesłania.',
    savingOffline:
      'Brak połączenia. Nagranie zostaje na tym urządzeniu i zostanie przesłane po ponownym połączeniu.',
    sealing: 'Zapisywanie nagrania',
    recordingNow: 'Trwa nagrywanie',
    recordingElsewhere: 'To spotkanie jest nagrywane na innym urządzeniu.',
    openRecording: 'Otwórz',
    discardRecording: 'Odrzuć nagranie',
    resume: 'Wznów nagrywanie',
    stopHere: 'Zatrzymaj i zapisz',
    recoveryTitle: 'Nagrywanie zostało przerwane',
    recoveryBody:
      'Dźwięk nagrany przed przerwą jest zapisany. Wznów nagrywanie albo je zatrzymaj i zapisz.',
    tabEnded: 'Karta spotkania nie jest już udostępniana. Nagrywanie trwa z mikrofonu.',
    micEnded: 'Mikrofon przestał działać. Nagrywanie trwa z karty spotkania.',
    micLost: 'Mikrofon przestał działać, więc nagrywanie wstrzymano.',
    limitWarning: 'Nagrywanie zakończy się za {minutes} min.',
    limitReached: 'Nagranie osiągnęło maksymalną długość i zostało zatrzymane.',
    previewCaption: 'Podgląd na żywo. Ostateczna transkrypcja powstaje z nagrania.',
    previewPaused: 'Podgląd na żywo wstrzymany.',
    previewLag: 'Tekst na żywo jest opóźniony o {seconds} s.',
    previewNotShown: '{seconds} s dźwięku nie zostało pokazane na żywo. Nadal jest nagrane.',
    previewUnavailable: 'Podgląd na żywo jest niedostępny. Nagrywanie trwa.',
    micDenied: 'Zezwól na dostęp do mikrofonu i spróbuj ponownie.',
    noMic: 'Nie znaleziono mikrofonu.',
    micBusy: 'Mikrofon jest używany przez inną aplikację.',
    insecureOrigin: 'Nagrywanie wymaga bezpiecznego połączenia (https).',
    shareCancelled: 'Udostępnianie karty zostało anulowane.',
    shareNoAudio:
      'Wybierz kartę spotkania z włączonym „Udostępnij dźwięk karty”. Okno ani cały ekran nie mogą służyć do nagrywania dźwięku karty spotkania.',
    tabUnsupported:
      'Ta przeglądarka nie może nagrywać dźwięku karty spotkania. Użyj tutaj mikrofonu, otwórz spotkanie i Poneva w Chrome lub Edge na komputerze albo zaimportuj nagranie.',
    storageLow: 'Na tym urządzeniu kończy się miejsce. Zwolnij miejsce przed nagrywaniem.',
    captureClosed: 'To nagrywanie zostało już zatrzymane.',
    captureActiveElsewhere: 'Już nagrywasz inne spotkanie.',
    recordUnavailable: 'Nagrywanie jest teraz niedostępne.',
    stopFailed:
      'Nie udało się zatrzymać nagrywania. Sprawdź połączenie lub zaloguj się ponownie, a potem spróbuj jeszcze raz.',
    signOutRecordingTitle: 'Nagrywanie spotkania wciąż trwa',
    signOutRecordingBody:
      'Wylogowanie je zatrzyma. Dźwięk, który nie został jeszcze przesłany, zostanie na tym urządzeniu i prześle się przy następnym logowaniu tutaj.',
    signOutAnyway: 'Wyloguj się',
    paymentRequired:
      'Kredyt na korzystanie Twojej firmy się wyczerpał. Administrator może dodać kartę, a potem spróbuj ponownie.',
  },
  pt: {
    pageEyebrow: 'Espaço de trabalho de {company}',
    pageTitle: 'Reuniões',
    pageDescription:
      'Grave uma reunião aqui, ou importe uma gravação ou transcrição. A Poneva guarda o original e prepara notas com fontes usando o contexto da sua empresa.',
    loadingMeetings: 'A carregar reuniões…',
    loadFailed: 'Não foi possível carregar as reuniões.',
    emptyTitle: 'Cada reunião, pronta a usar.',
    emptyDescription:
      'Grave uma reunião, ou importe uma gravação ou transcrição. O original continua disponível enquanto a Poneva prepara a transcrição e os próximos passos com fontes.',
    importMeeting: 'Importar reunião',
    importFirst: 'Importar a sua primeira reunião',
    importUnavailable:
      'A importação de áudio não está disponível neste servidor. Ainda pode importar transcrições.',
    rowRecording: 'A gravar… {time}',
    rowSaving: 'A guardar…',
    importDialogTitle: 'Importar uma reunião',
    importRetainedNote:
      'A gravação original é mantida. O áudio é processado numa cópia de voz mais pequena sem substituir o original.',
    importSaved: 'Reunião guardada. A transcrição decorre em segundo plano.',
    importFailed: 'Não foi possível importar esta reunião.',
    uploadProgress: 'Progresso do envio',
    uploadingPercent: 'A enviar {percent}%',
    titleLabel: 'Título',
    membersLabel: 'Pessoas de {company}',
    removeMember: 'Remover {name}',
    searchMembers: 'Procurar membros da empresa',
    you: '(você)',
    noMatchingMembers: 'Nenhum membro corresponde.',
    guestsLabel: 'Outros participantes',
    optional: '(opcional)',
    guestsPlaceholder: 'Um nome ou e-mail por linha',
    cancel: 'Cancelar',
    close: 'Fechar',
    dismiss: 'Dispensar',
    retryStop: 'Tentar novamente',
    record: 'Gravar',
    starting: 'A iniciar…',
    recordDialogTitle: 'Gravar uma reunião',
    recordDialogNote:
      'O áudio é guardado no Chat enquanto grava. A transcrição e as notas são criadas a partir da gravação quando parar.',
    sourceLabel: 'O que gravar',
    sourceRoom: 'Esta sala',
    sourceRoomHint: 'O microfone deste dispositivo.',
    sourceTab: 'Um separador de reunião',
    sourceTabHint: 'O som de uma chamada noutro separador do navegador.',
    sourceBoth: 'Ambos',
    sourceBothHint: 'O separador da chamada e o microfone deste dispositivo.',
    tabPickerNote:
      'Escolha o separador da reunião e ative «Partilhar áudio do separador». O navegador chama a isto partilha de ecrã, mas a Poneva grava apenas áudio e para o vídeo imediatamente. Escolha «Ambos» para incluir o microfone.',
    consentLine: 'Todos na reunião sabem que está a ser gravada.',
    startRecording: 'Iniciar gravação',
    defaultTitle: 'Reunião {date}',
    stop: 'Parar',
    stopRecordingLabel: 'Parar a gravação ({time})',
    inputLevel: 'Nível do microfone',
    elapsed: 'A gravar {time}',
    uploadBacklog: '{seconds} s de áudio à espera de envio.',
    saving: 'A guardar a gravação… Faltam enviar {seconds} s de áudio.',
    savingOffline:
      'Sem ligação. A gravação fica neste dispositivo e é enviada quando voltar a ligar-se.',
    sealing: 'A guardar a gravação',
    recordingNow: 'Gravação em curso',
    recordingElsewhere: 'Esta reunião está a ser gravada noutro dispositivo.',
    openRecording: 'Abrir',
    discardRecording: 'Descartar a gravação',
    resume: 'Retomar a gravação',
    stopHere: 'Parar e guardar',
    recoveryTitle: 'Uma gravação foi interrompida',
    recoveryBody:
      'O áudio gravado antes da interrupção está guardado. Retome a gravação, ou pare e guarde-a.',
    tabEnded:
      'O separador da reunião deixou de ser partilhado. A gravação continua a partir do microfone.',
    micEnded: 'O microfone parou. A gravação continua a partir do separador da reunião.',
    micLost: 'O microfone parou, por isso a gravação foi pausada.',
    limitWarning: 'A gravação para daqui a {minutes} min.',
    limitReached: 'A gravação atingiu a duração máxima e foi parada.',
    previewCaption: 'Pré-visualização em direto. A transcrição final é feita a partir da gravação.',
    previewPaused: 'Pré-visualização em direto em pausa.',
    previewLag: 'O texto em direto está {seconds} s atrasado.',
    previewNotShown: '{seconds} s de áudio não foram mostrados em direto. Continuam gravados.',
    previewUnavailable: 'A pré-visualização em direto não está disponível. A gravação continua.',
    micDenied: 'Permita o microfone e tente novamente.',
    noMic: 'Não foi encontrado nenhum microfone.',
    micBusy: 'O microfone está a ser usado por outra aplicação.',
    insecureOrigin: 'A gravação precisa de uma ligação segura (https).',
    shareCancelled: 'A partilha do separador foi cancelada.',
    shareNoAudio:
      'Escolha o separador da reunião com «Partilhar áudio do separador» ativo. Uma janela ou o ecrã inteiro não podem ser usados para áudio de um separador de reunião.',
    tabUnsupported:
      'Este navegador não consegue captar áudio de um separador de reunião. Use o microfone aqui, abra a reunião e a Poneva no Chrome ou Edge num computador, ou importe uma gravação.',
    storageLow: 'Este dispositivo tem pouco espaço. Liberte espaço antes de gravar.',
    captureClosed: 'Esta gravação já foi parada.',
    captureActiveElsewhere: 'Já está a gravar outra reunião.',
    recordUnavailable: 'A gravação não está disponível neste momento.',
    stopFailed:
      'Não foi possível parar a gravação. Verifique a ligação ou inicie sessão novamente e tente outra vez.',
    signOutRecordingTitle: 'A gravação de uma reunião ainda está em curso',
    signOutRecordingBody:
      'Terminar sessão para a gravação. O áudio ainda não carregado fica neste dispositivo e termina de ser carregado da próxima vez que iniciar sessão aqui.',
    signOutAnyway: 'Terminar sessão',
    paymentRequired:
      'O crédito de utilização da sua empresa esgotou-se. Um administrador pode adicionar um cartão; depois tente novamente.',
  },
  ro: {
    pageEyebrow: 'Spațiul de lucru {company}',
    pageTitle: 'Ședințe',
    pageDescription:
      'Înregistrează aici o ședință sau importă o înregistrare ori o transcriere. Poneva păstrează originalul și pregătește note cu surse pe baza contextului companiei tale.',
    loadingMeetings: 'Se încarcă ședințele…',
    loadFailed: 'Nu am putut încărca ședințele.',
    emptyTitle: 'Fiecare ședință, gata de folosit.',
    emptyDescription:
      'Înregistrează o ședință sau importă o înregistrare ori o transcriere. Originalul rămâne disponibil cât timp Poneva pregătește transcrierea și pașii următori cu surse.',
    importMeeting: 'Importă ședința',
    importFirst: 'Importă prima ședință',
    importUnavailable:
      'Importul audio nu este disponibil pe acest server. Poți importa în continuare transcrieri.',
    rowRecording: 'Se înregistrează… {time}',
    rowSaving: 'Se salvează…',
    importDialogTitle: 'Importă o ședință',
    importRetainedNote:
      'Înregistrarea originală se păstrează. Sunetul este prelucrat într-o copie de vorbire mai mică, fără a înlocui originalul.',
    importSaved: 'Ședința a fost salvată. Transcrierea rulează în fundal.',
    importFailed: 'Nu am putut importa această ședință.',
    uploadProgress: 'Progresul încărcării',
    uploadingPercent: 'Se încarcă {percent}%',
    titleLabel: 'Titlu',
    membersLabel: 'Persoane din {company}',
    removeMember: 'Elimină: {name}',
    searchMembers: 'Caută membri ai companiei',
    you: '(tu)',
    noMatchingMembers: 'Niciun membru corespunzător.',
    guestsLabel: 'Alți participanți',
    optional: '(opțional)',
    guestsPlaceholder: 'Câte un nume sau e-mail pe rând',
    cancel: 'Anulează',
    close: 'Închide',
    dismiss: 'Respinge',
    retryStop: 'Încearcă din nou',
    record: 'Înregistrează',
    starting: 'Se pornește…',
    recordDialogTitle: 'Înregistrează o ședință',
    recordDialogNote:
      'Sunetul se salvează în Chat în timp ce înregistrezi. Transcrierea și notele se fac din înregistrare după ce te oprești.',
    sourceLabel: 'Ce se înregistrează',
    sourceRoom: 'Această încăpere',
    sourceRoomHint: 'Microfonul acestui dispozitiv.',
    sourceTab: 'O filă de ședință',
    sourceTabHint: 'Sunetul unui apel dintr-o altă filă a browserului.',
    sourceBoth: 'Ambele',
    sourceBothHint: 'Fila apelului și microfonul acestui dispozitiv.',
    tabPickerNote:
      'Alege fila întâlnirii și activează „Partajează sunetul filei”. Browserul numește asta partajarea ecranului, dar Poneva înregistrează doar sunetul și oprește imediat imaginea video. Alege „Ambele” pentru a include microfonul.',
    consentLine: 'Toți participanții știu că ședința este înregistrată.',
    startRecording: 'Pornește înregistrarea',
    defaultTitle: 'Ședință {date}',
    stop: 'Oprește',
    stopRecordingLabel: 'Oprește înregistrarea ({time})',
    inputLevel: 'Nivelul microfonului',
    elapsed: 'Se înregistrează {time}',
    uploadBacklog: '{seconds} s de sunet așteaptă încărcarea.',
    saving: 'Se salvează înregistrarea… Mai sunt de încărcat {seconds} s de sunet.',
    savingOffline:
      'Offline. Înregistrarea rămâne pe acest dispozitiv și se încarcă după ce te reconectezi.',
    sealing: 'Se salvează înregistrarea',
    recordingNow: 'Înregistrare în curs',
    recordingElsewhere: 'Această ședință este înregistrată pe alt dispozitiv.',
    openRecording: 'Deschide',
    discardRecording: 'Renunță la înregistrare',
    resume: 'Reia înregistrarea',
    stopHere: 'Oprește și salvează',
    recoveryTitle: 'O înregistrare a fost întreruptă',
    recoveryBody:
      'Sunetul înregistrat înainte de întrerupere este salvat. Reia înregistrarea sau oprește-o și salveaz-o.',
    tabEnded: 'Fila ședinței nu mai este partajată. Înregistrarea continuă de la microfon.',
    micEnded: 'Microfonul s-a oprit. Înregistrarea continuă din fila ședinței.',
    micLost: 'Microfonul s-a oprit, așa că înregistrarea a fost întreruptă temporar.',
    limitWarning: 'Înregistrarea se oprește în {minutes} min.',
    limitReached: 'Înregistrarea a atins durata maximă și a fost oprită.',
    previewCaption: 'Previzualizare live. Transcrierea finală se face din înregistrare.',
    previewPaused: 'Previzualizarea live este în pauză.',
    previewLag: 'Textul live are o întârziere de {seconds} s.',
    previewNotShown: '{seconds} s de sunet nu au fost afișate live. Sunt totuși înregistrate.',
    previewUnavailable: 'Previzualizarea live nu este disponibilă. Înregistrarea continuă.',
    micDenied: 'Permite accesul la microfon și încearcă din nou.',
    noMic: 'Nu a fost găsit niciun microfon.',
    micBusy: 'Microfonul este folosit de altă aplicație.',
    insecureOrigin: 'Înregistrarea necesită o conexiune securizată (https).',
    shareCancelled: 'Partajarea filei a fost anulată.',
    shareNoAudio:
      'Alege fila întâlnirii cu „Partajează sunetul filei” activat. O fereastră sau întregul ecran nu pot fi folosite pentru sunetul filei întâlnirii.',
    tabUnsupported:
      'Acest browser nu poate captura sunetul filei întâlnirii. Folosește microfonul aici, deschide întâlnirea și Poneva în Chrome sau Edge pe computer sau importă o înregistrare.',
    storageLow:
      'Spațiul de stocare al dispozitivului este aproape plin. Eliberează spațiu înainte de a înregistra.',
    captureClosed: 'Această înregistrare a fost deja oprită.',
    captureActiveElsewhere: 'Înregistrezi deja o altă ședință.',
    recordUnavailable: 'Înregistrarea nu este disponibilă acum.',
    stopFailed:
      'Înregistrarea nu a putut fi oprită. Verifică conexiunea sau conectează-te din nou, apoi încearcă iar.',
    signOutRecordingTitle: 'O înregistrare a unei întâlniri este încă în desfășurare',
    signOutRecordingBody:
      'Deconectarea o oprește. Sunetul care nu a fost încă încărcat rămâne pe acest dispozitiv și se încarcă data viitoare când te conectezi aici.',
    signOutAnyway: 'Deconectează-te',
    paymentRequired:
      'Creditul de utilizare al companiei tale s-a epuizat. Un administrator poate adăuga un card, apoi încearcă din nou.',
  },
  sk: {
    pageEyebrow: 'Pracovný priestor {company}',
    pageTitle: 'Stretnutia',
    pageDescription:
      'Nahrajte stretnutie tu alebo importujte nahrávku či prepis. Poneva si ponechá originál a pripraví poznámky so zdrojmi na základe kontextu vašej firmy.',
    loadingMeetings: 'Načítavajú sa stretnutia…',
    loadFailed: 'Stretnutia sa nepodarilo načítať.',
    emptyTitle: 'Každé stretnutie pripravené na použitie.',
    emptyDescription:
      'Nahrajte stretnutie alebo importujte nahrávku či prepis. Originál zostane dostupný, kým Poneva pripraví prepis a ďalšie kroky so zdrojmi.',
    importMeeting: 'Importovať stretnutie',
    importFirst: 'Importujte svoje prvé stretnutie',
    importUnavailable:
      'Import zvuku nie je na tomto serveri k dispozícii. Prepisy môžete importovať aj naďalej.',
    rowRecording: 'Nahráva sa… {time}',
    rowSaving: 'Ukladá sa…',
    importDialogTitle: 'Import stretnutia',
    importRetainedNote:
      'Pôvodná nahrávka sa zachová. Zvuk sa spracuje do menšej rečovej kópie bez toho, aby nahradil originál.',
    importSaved: 'Stretnutie je uložené. Prepis prebieha na pozadí.',
    importFailed: 'Toto stretnutie sa nepodarilo importovať.',
    uploadProgress: 'Priebeh nahrávania',
    uploadingPercent: 'Nahráva sa {percent} %',
    titleLabel: 'Názov',
    membersLabel: 'Ľudia z {company}',
    removeMember: 'Odstrániť: {name}',
    searchMembers: 'Hľadať členov firmy',
    you: '(vy)',
    noMatchingMembers: 'Žiadni zodpovedajúci členovia.',
    guestsLabel: 'Ďalší účastníci',
    optional: '(voliteľné)',
    guestsPlaceholder: 'Jedno meno alebo e-mail na riadok',
    cancel: 'Zrušiť',
    close: 'Zavrieť',
    dismiss: 'Skryť',
    retryStop: 'Skúsiť znova',
    record: 'Nahrať',
    starting: 'Spúšťa sa…',
    recordDialogTitle: 'Nahrať stretnutie',
    recordDialogNote:
      'Zvuk sa počas nahrávania ukladá do Chatu. Prepis a poznámky vzniknú z nahrávky po zastavení.',
    sourceLabel: 'Čo nahrať',
    sourceRoom: 'Táto miestnosť',
    sourceRoomHint: 'Mikrofón tohto zariadenia.',
    sourceTab: 'Karta so stretnutím',
    sourceTabHint: 'Zvuk hovoru v inej karte prehliadača.',
    sourceBoth: 'Oboje',
    sourceBothHint: 'Karta s hovorom a mikrofón tohto zariadenia.',
    tabPickerNote:
      'Vyberte kartu schôdze a zapnite „Zdieľať zvuk karty“. Prehliadač to nazýva zdieľaním obrazovky, ale Poneva nahráva iba zvuk a ihneď zastaví video. Na zahrnutie mikrofónu vyberte „Oboje“.',
    consentLine: 'Všetci účastníci vedia, že sa stretnutie nahráva.',
    startRecording: 'Spustiť nahrávanie',
    defaultTitle: 'Stretnutie {date}',
    stop: 'Zastaviť',
    stopRecordingLabel: 'Zastaviť nahrávanie ({time})',
    inputLevel: 'Úroveň mikrofónu',
    elapsed: 'Nahrávanie {time}',
    uploadBacklog: '{seconds} s zvuku čaká na nahratie.',
    saving: 'Ukladá sa nahrávka… Zostáva nahrať {seconds} s zvuku.',
    savingOffline:
      'Offline. Nahrávka zostane v tomto zariadení a nahrá sa, keď budete znova pripojení.',
    sealing: 'Ukladá sa nahrávka',
    recordingNow: 'Prebieha nahrávanie',
    recordingElsewhere: 'Toto stretnutie sa nahráva na inom zariadení.',
    openRecording: 'Otvoriť',
    discardRecording: 'Zahodiť nahrávku',
    resume: 'Pokračovať v nahrávaní',
    stopHere: 'Zastaviť a uložiť',
    recoveryTitle: 'Nahrávanie bolo prerušené',
    recoveryBody:
      'Zvuk nahraný pred prerušením je uložený. Pokračujte v nahrávaní alebo ho zastavte a uložte.',
    tabEnded: 'Karta so stretnutím sa prestala zdieľať. Nahrávanie pokračuje z mikrofónu.',
    micEnded: 'Mikrofón prestal fungovať. Nahrávanie pokračuje z karty so stretnutím.',
    micLost: 'Mikrofón prestal fungovať, preto sa nahrávanie pozastavilo.',
    limitWarning: 'Nahrávanie sa skončí o {minutes} min.',
    limitReached: 'Nahrávka dosiahla maximálnu dĺžku a bola zastavená.',
    previewCaption: 'Živý náhľad. Konečný prepis vznikne z nahrávky.',
    previewPaused: 'Živý náhľad je pozastavený.',
    previewLag: 'Živý text mešká o {seconds} s.',
    previewNotShown: '{seconds} s zvuku sa nezobrazilo naživo. Stále je nahratých.',
    previewUnavailable: 'Živý náhľad nie je k dispozícii. Nahrávanie pokračuje.',
    micDenied: 'Povoľte mikrofón a skúste to znova.',
    noMic: 'Nenašiel sa žiadny mikrofón.',
    micBusy: 'Mikrofón používa iná aplikácia.',
    insecureOrigin: 'Nahrávanie vyžaduje zabezpečené pripojenie (https).',
    shareCancelled: 'Zdieľanie karty bolo zrušené.',
    shareNoAudio:
      'Vyberte kartu schôdze so zapnutým „Zdieľať zvuk karty“. Okno ani celú obrazovku nemožno použiť na zvuk karty schôdze.',
    tabUnsupported:
      'Tento prehliadač nedokáže nahrávať zvuk karty schôdze. Použite tu mikrofón, otvorte schôdzu a Ponevu v Chrome alebo Edge na počítači alebo importujte nahrávku.',
    storageLow: 'V zariadení dochádza miesto. Pred nahrávaním uvoľnite miesto.',
    captureClosed: 'Toto nahrávanie už bolo zastavené.',
    captureActiveElsewhere: 'Už nahrávate iné stretnutie.',
    recordUnavailable: 'Nahrávanie teraz nie je k dispozícii.',
    stopFailed:
      'Nahrávanie sa nepodarilo zastaviť. Skontrolujte pripojenie alebo sa znova prihláste a skúste to znova.',
    signOutRecordingTitle: 'Nahrávanie stretnutia stále prebieha',
    signOutRecordingBody:
      'Odhlásením sa zastaví. Zvuk, ktorý ešte nebol nahraný, zostane v tomto zariadení a nahrá sa pri ďalšom prihlásení tu.',
    signOutAnyway: 'Odhlásiť sa',
    paymentRequired:
      'Kredit na využívanie vašej firmy je vyčerpaný. Správca môže pridať kartu, potom to skúste znova.',
  },
  sl: {
    pageEyebrow: 'Delovni prostor {company}',
    pageTitle: 'Sestanki',
    pageDescription:
      'Tukaj posnemite sestanek ali uvozite posnetek ali prepis. Poneva ohrani izvirnik in pripravi zapiske z viri na podlagi konteksta vašega podjetja.',
    loadingMeetings: 'Nalaganje sestankov …',
    loadFailed: 'Sestankov ni bilo mogoče naložiti.',
    emptyTitle: 'Vsak sestanek, pripravljen za uporabo.',
    emptyDescription:
      'Posnemite sestanek ali uvozite posnetek ali prepis. Izvirnik ostane na voljo, medtem ko Poneva pripravi prepis in nadaljnje korake z viri.',
    importMeeting: 'Uvozi sestanek',
    importFirst: 'Uvozite svoj prvi sestanek',
    importUnavailable: 'Uvoz zvoka na tem strežniku ni na voljo. Prepise lahko še vedno uvozite.',
    rowRecording: 'Snemanje … {time}',
    rowSaving: 'Shranjevanje …',
    importDialogTitle: 'Uvoz sestanka',
    importRetainedNote:
      'Izvirni posnetek se ohrani. Zvok se obdela v manjšo govorno kopijo, ne da bi nadomestil izvirnik.',
    importSaved: 'Sestanek je shranjen. Prepisovanje poteka v ozadju.',
    importFailed: 'Tega sestanka ni bilo mogoče uvoziti.',
    uploadProgress: 'Napredek nalaganja',
    uploadingPercent: 'Nalaganje {percent} %',
    titleLabel: 'Naslov',
    membersLabel: 'Osebe iz {company}',
    removeMember: 'Odstrani: {name}',
    searchMembers: 'Iskanje članov podjetja',
    you: '(vi)',
    noMatchingMembers: 'Ni ujemajočih se članov.',
    guestsLabel: 'Drugi udeleženci',
    optional: '(neobvezno)',
    guestsPlaceholder: 'Eno ime ali e-poštni naslov na vrstico',
    cancel: 'Prekliči',
    close: 'Zapri',
    dismiss: 'Opusti',
    retryStop: 'Poskusi znova',
    record: 'Snemaj',
    starting: 'Zaganjanje …',
    recordDialogTitle: 'Snemanje sestanka',
    recordDialogNote:
      'Zvok se med snemanjem shranjuje v Chat. Prepis in zapiski nastanejo iz posnetka, ko ustavite snemanje.',
    sourceLabel: 'Kaj posneti',
    sourceRoom: 'Ta prostor',
    sourceRoomHint: 'Mikrofon te naprave.',
    sourceTab: 'Zavihek sestanka',
    sourceTabHint: 'Zvok klica v drugem zavihku brskalnika.',
    sourceBoth: 'Oboje',
    sourceBothHint: 'Zavihek klica in mikrofon te naprave.',
    tabPickerNote:
      'Izberite zavihek sestanka in vklopite »Deli zvok zavihka«. Brskalnik to imenuje deljenje zaslona, vendar Poneva snema samo zvok in takoj ustavi video. Izberite »Oboje«, da vključite mikrofon.',
    consentLine: 'Vsi na sestanku vedo, da se snema.',
    startRecording: 'Začni snemanje',
    defaultTitle: 'Sestanek {date}',
    stop: 'Ustavi',
    stopRecordingLabel: 'Ustavi snemanje ({time})',
    inputLevel: 'Raven mikrofona',
    elapsed: 'Snemanje {time}',
    uploadBacklog: '{seconds} s zvoka čaka na nalaganje.',
    saving: 'Shranjevanje posnetka … Naložiti je treba še {seconds} s zvoka.',
    savingOffline:
      'Brez povezave. Posnetek ostane v tej napravi in se naloži, ko se znova povežete.',
    sealing: 'Shranjevanje posnetka',
    recordingNow: 'Snemanje poteka',
    recordingElsewhere: 'Ta sestanek se snema v drugi napravi.',
    openRecording: 'Odpri',
    discardRecording: 'Zavrzi posnetek',
    resume: 'Nadaljuj snemanje',
    stopHere: 'Ustavi in shrani',
    recoveryTitle: 'Snemanje je bilo prekinjeno',
    recoveryBody:
      'Zvok, posnet pred prekinitvijo, je shranjen. Nadaljujte snemanje ali ga ustavite in shranite.',
    tabEnded: 'Zavihek sestanka se ne deli več. Snemanje se nadaljuje z mikrofonom.',
    micEnded: 'Mikrofon se je ustavil. Snemanje se nadaljuje z zavihkom sestanka.',
    micLost: 'Mikrofon se je ustavil, zato je bilo snemanje začasno ustavljeno.',
    limitWarning: 'Snemanje se bo ustavilo čez {minutes} min.',
    limitReached: 'Posnetek je dosegel največjo dolžino in je bil ustavljen.',
    previewCaption: 'Predogled v živo. Končni prepis nastane iz posnetka.',
    previewPaused: 'Predogled v živo je začasno ustavljen.',
    previewLag: 'Besedilo v živo zamuja {seconds} s.',
    previewNotShown: '{seconds} s zvoka ni bilo prikazanih v živo. Še vedno je posneto.',
    previewUnavailable: 'Predogled v živo ni na voljo. Snemanje se nadaljuje.',
    micDenied: 'Dovolite dostop do mikrofona in poskusite znova.',
    noMic: 'Mikrofona ni bilo mogoče najti.',
    micBusy: 'Mikrofon uporablja druga aplikacija.',
    insecureOrigin: 'Za snemanje je potrebna varna povezava (https).',
    shareCancelled: 'Deljenje zavihka je bilo preklicano.',
    shareNoAudio:
      'Izberite zavihek sestanka z vklopljenim »Deli zvok zavihka«. Okna ali celotnega zaslona ni mogoče uporabiti za zvok zavihka sestanka.',
    tabUnsupported:
      'Ta brskalnik ne more zajeti zvoka zavihka sestanka. Tukaj uporabite mikrofon, odprite sestanek in Ponevo v Chromu ali Edgeu na računalniku ali uvozite posnetek.',
    storageLow: 'V tej napravi zmanjkuje prostora. Pred snemanjem sprostite prostor.',
    captureClosed: 'To snemanje je že ustavljeno.',
    captureActiveElsewhere: 'Že snemate drug sestanek.',
    recordUnavailable: 'Snemanje trenutno ni na voljo.',
    stopFailed:
      'Snemanja ni bilo mogoče ustaviti. Preverite povezavo ali se znova prijavite in poskusite znova.',
    signOutRecordingTitle: 'Snemanje sestanka še poteka',
    signOutRecordingBody:
      'Odjava ga ustavi. Zvok, ki še ni naložen, ostane v tej napravi in se naloži, ko se naslednjič prijavite tukaj.',
    signOutAnyway: 'Odjava',
    paymentRequired:
      'Kredit za uporabo vašega podjetja je porabljen. Skrbnik lahko doda kartico, nato poskusite znova.',
  },
  es: {
    pageEyebrow: 'Espacio de trabajo de {company}',
    pageTitle: 'Reuniones',
    pageDescription:
      'Graba una reunión aquí o importa una grabación o una transcripción. Poneva conserva el original y prepara notas con fuentes a partir del contexto de tu empresa.',
    loadingMeetings: 'Cargando reuniones…',
    loadFailed: 'No pudimos cargar las reuniones.',
    emptyTitle: 'Cada reunión, lista para usar.',
    emptyDescription:
      'Graba una reunión o importa una grabación o una transcripción. El original sigue disponible mientras Poneva prepara la transcripción y los siguientes pasos con fuentes.',
    importMeeting: 'Importar reunión',
    importFirst: 'Importa tu primera reunión',
    importUnavailable:
      'La importación de audio no está disponible en este servidor. Aún puedes importar transcripciones.',
    rowRecording: 'Grabando… {time}',
    rowSaving: 'Guardando…',
    importDialogTitle: 'Importar una reunión',
    importRetainedNote:
      'Se conserva la grabación original. El audio se procesa en una copia de voz más pequeña sin sustituir el original.',
    importSaved: 'Reunión guardada. La transcripción se ejecuta en segundo plano.',
    importFailed: 'No pudimos importar esta reunión.',
    uploadProgress: 'Progreso de la subida',
    uploadingPercent: 'Subiendo {percent} %',
    titleLabel: 'Título',
    membersLabel: 'Personas de {company}',
    removeMember: 'Quitar a {name}',
    searchMembers: 'Buscar miembros de la empresa',
    you: '(tú)',
    noMatchingMembers: 'No hay miembros que coincidan.',
    guestsLabel: 'Otros asistentes',
    optional: '(opcional)',
    guestsPlaceholder: 'Un nombre o correo electrónico por línea',
    cancel: 'Cancelar',
    close: 'Cerrar',
    dismiss: 'Descartar',
    retryStop: 'Reintentar',
    record: 'Grabar',
    starting: 'Iniciando…',
    recordDialogTitle: 'Grabar una reunión',
    recordDialogNote:
      'El audio se guarda en Chat mientras grabas. La transcripción y las notas se crean a partir de la grabación cuando te detienes.',
    sourceLabel: 'Qué grabar',
    sourceRoom: 'Esta sala',
    sourceRoomHint: 'El micrófono de este dispositivo.',
    sourceTab: 'Una pestaña de reunión',
    sourceTabHint: 'El sonido de una llamada en otra pestaña del navegador.',
    sourceBoth: 'Ambos',
    sourceBothHint: 'La pestaña de la llamada y el micrófono de este dispositivo.',
    tabPickerNote:
      'Elige la pestaña de la reunión y activa «Compartir audio de la pestaña». El navegador lo llama compartir pantalla, pero Poneva graba solo audio y detiene el vídeo de inmediato. Elige «Ambos» para incluir tu micrófono.',
    consentLine: 'Todos en la reunión saben que se está grabando.',
    startRecording: 'Empezar a grabar',
    defaultTitle: 'Reunión {date}',
    stop: 'Detener',
    stopRecordingLabel: 'Detener la grabación ({time})',
    inputLevel: 'Nivel del micrófono',
    elapsed: 'Grabando {time}',
    uploadBacklog: '{seconds} s de audio pendientes de subir.',
    saving: 'Guardando la grabación… Quedan {seconds} s de audio por subir.',
    savingOffline:
      'Sin conexión. La grabación se guarda en este dispositivo y se subirá cuando vuelvas a conectarte.',
    sealing: 'Guardando la grabación',
    recordingNow: 'Grabación en curso',
    recordingElsewhere: 'Esta reunión se está grabando en otro dispositivo.',
    openRecording: 'Abrir',
    discardRecording: 'Descartar la grabación',
    resume: 'Reanudar la grabación',
    stopHere: 'Detener y guardar',
    recoveryTitle: 'Se interrumpió una grabación',
    recoveryBody:
      'El audio grabado antes de la interrupción está guardado. Reanuda la grabación o detenla y guárdala.',
    tabEnded:
      'La pestaña de la reunión dejó de compartirse. La grabación continúa con el micrófono.',
    micEnded: 'El micrófono se detuvo. La grabación continúa con la pestaña de la reunión.',
    micLost: 'El micrófono se detuvo, así que la grabación se pausó.',
    limitWarning: 'La grabación se detendrá en {minutes} min.',
    limitReached: 'La grabación alcanzó su duración máxima y se detuvo.',
    previewCaption:
      'Vista previa en directo. La transcripción final se hace a partir de la grabación.',
    previewPaused: 'Vista previa en directo en pausa.',
    previewLag: 'El texto en directo va {seconds} s por detrás.',
    previewNotShown: '{seconds} s de audio no se mostraron en directo. Siguen grabados.',
    previewUnavailable: 'La vista previa en directo no está disponible. La grabación continúa.',
    micDenied: 'Permite el acceso al micrófono e inténtalo de nuevo.',
    noMic: 'No se encontró ningún micrófono.',
    micBusy: 'Otra aplicación está usando el micrófono.',
    insecureOrigin: 'Para grabar se necesita una conexión segura (https).',
    shareCancelled: 'Se canceló el uso compartido de la pestaña.',
    shareNoAudio:
      'Elige la pestaña de la reunión con «Compartir audio de la pestaña» activado. Una ventana o toda la pantalla no sirven para capturar audio de una pestaña de reunión.',
    tabUnsupported:
      'Este navegador no puede capturar el audio de una pestaña de reunión. Usa el micrófono aquí, abre la reunión y Poneva en Chrome o Edge en un ordenador, o importa una grabación.',
    storageLow: 'Este dispositivo tiene poco espacio. Libera espacio antes de grabar.',
    captureClosed: 'Esta grabación ya se ha detenido.',
    captureActiveElsewhere: 'Ya estás grabando otra reunión.',
    recordUnavailable: 'La grabación no está disponible en este momento.',
    stopFailed:
      'No se pudo detener la grabación. Comprueba la conexión o vuelve a iniciar sesión e inténtalo de nuevo.',
    signOutRecordingTitle: 'Todavía hay una grabación de reunión en curso',
    signOutRecordingBody:
      'Cerrar sesión la detiene. El audio que aún no se ha subido se queda en este dispositivo y se termina de subir la próxima vez que inicies sesión aquí.',
    signOutAnyway: 'Cerrar sesión',
    paymentRequired:
      'El crédito de uso de tu empresa se ha agotado. Un administrador puede añadir una tarjeta; después, vuelve a intentarlo.',
  },
  sv: {
    pageEyebrow: 'Arbetsyta för {company}',
    pageTitle: 'Möten',
    pageDescription:
      'Spela in ett möte här, eller importera en inspelning eller ett transkript. Poneva behåller originalet och tar fram anteckningar med källhänvisningar utifrån företagets sammanhang.',
    loadingMeetings: 'Läser in möten…',
    loadFailed: 'Vi kunde inte läsa in mötena.',
    emptyTitle: 'Varje möte, redo att använda.',
    emptyDescription:
      'Spela in ett möte, eller importera en inspelning eller ett transkript. Originalet finns kvar medan Poneva tar fram transkriptet och uppföljningar med källhänvisningar.',
    importMeeting: 'Importera möte',
    importFirst: 'Importera ditt första möte',
    importUnavailable:
      'Import av ljud är inte tillgängligt på den här servern. Du kan fortfarande importera transkript.',
    rowRecording: 'Spelar in… {time}',
    rowSaving: 'Sparar…',
    importDialogTitle: 'Importera ett möte',
    importRetainedNote:
      'Originalinspelningen sparas. Ljudet bearbetas till en mindre talkopia utan att ersätta originalet.',
    importSaved: 'Mötet är sparat. Transkriberingen pågår i bakgrunden.',
    importFailed: 'Vi kunde inte importera det här mötet.',
    uploadProgress: 'Uppladdningsförlopp',
    uploadingPercent: 'Laddar upp {percent} %',
    titleLabel: 'Titel',
    membersLabel: 'Personer från {company}',
    removeMember: 'Ta bort {name}',
    searchMembers: 'Sök bland företagets medlemmar',
    you: '(du)',
    noMatchingMembers: 'Inga matchande medlemmar.',
    guestsLabel: 'Andra deltagare',
    optional: '(valfritt)',
    guestsPlaceholder: 'Ett namn eller en e-postadress per rad',
    cancel: 'Avbryt',
    close: 'Stäng',
    dismiss: 'Stäng',
    retryStop: 'Försök igen',
    record: 'Spela in',
    starting: 'Startar…',
    recordDialogTitle: 'Spela in ett möte',
    recordDialogNote:
      'Ljudet sparas i Chat medan du spelar in. Transkriptet och anteckningarna tas fram från inspelningen när du har stoppat.',
    sourceLabel: 'Vad ska spelas in',
    sourceRoom: 'Det här rummet',
    sourceRoomHint: 'Den här enhetens mikrofon.',
    sourceTab: 'En mötesflik',
    sourceTabHint: 'Ljudet från ett samtal i en annan webbläsarflik.',
    sourceBoth: 'Båda',
    sourceBothHint: 'Samtalsfliken och den här enhetens mikrofon.',
    tabPickerNote:
      'Välj mötets webbläsarflik och slå på ”Dela flikens ljud”. Webbläsaren kallar det skärmdelning, men Poneva spelar bara in ljud och stoppar videon direkt. Välj ”Båda” för att få med din mikrofon.',
    consentLine: 'Alla i mötet vet att det spelas in.',
    startRecording: 'Starta inspelningen',
    defaultTitle: 'Möte {date}',
    stop: 'Stoppa',
    stopRecordingLabel: 'Stoppa inspelningen ({time})',
    inputLevel: 'Mikrofonnivå',
    elapsed: 'Spelar in {time}',
    uploadBacklog: '{seconds} s ljud väntar på att laddas upp.',
    saving: 'Sparar inspelningen… {seconds} s ljud kvar att ladda upp.',
    savingOffline:
      'Offline. Inspelningen finns kvar på den här enheten och laddas upp när du är ansluten igen.',
    sealing: 'Sparar inspelningen',
    recordingNow: 'Inspelning pågår',
    recordingElsewhere: 'Det här mötet spelas in på en annan enhet.',
    openRecording: 'Öppna',
    discardRecording: 'Släng inspelningen',
    resume: 'Fortsätt spela in',
    stopHere: 'Stoppa och spara',
    recoveryTitle: 'En inspelning avbröts',
    recoveryBody:
      'Ljudet som spelades in före avbrottet är sparat. Fortsätt spela in, eller stoppa och spara det.',
    tabEnded: 'Mötesfliken slutade delas. Inspelningen fortsätter från mikrofonen.',
    micEnded: 'Mikrofonen slutade fungera. Inspelningen fortsätter från mötesfliken.',
    micLost: 'Mikrofonen slutade fungera, så inspelningen pausades.',
    limitWarning: 'Inspelningen stoppas om {minutes} min.',
    limitReached: 'Inspelningen nådde sin maxlängd och stoppades.',
    previewCaption: 'Liveförhandsvisning. Det slutliga transkriptet görs från inspelningen.',
    previewPaused: 'Liveförhandsvisningen är pausad.',
    previewLag: 'Livetexten ligger {seconds} s efter.',
    previewNotShown: '{seconds} s ljud visades inte live. Det är fortfarande inspelat.',
    previewUnavailable: 'Liveförhandsvisning är inte tillgänglig. Inspelningen fortsätter.',
    micDenied: 'Tillåt mikrofonen och försök igen.',
    noMic: 'Ingen mikrofon hittades.',
    micBusy: 'Mikrofonen används av en annan app.',
    insecureOrigin: 'Inspelning kräver en säker (https) anslutning.',
    shareCancelled: 'Delningen av fliken avbröts.',
    shareNoAudio:
      'Välj mötets webbläsarflik med ”Dela flikens ljud” påslaget. Ett fönster eller hela skärmen kan inte användas för ljud från en mötesflik.',
    tabUnsupported:
      'Den här webbläsaren kan inte spela in ljud från en mötesflik. Använd mikrofonen här, öppna mötet och Poneva i Chrome eller Edge på en dator, eller importera en inspelning.',
    storageLow: 'Enheten har ont om lagringsutrymme. Frigör utrymme innan du spelar in.',
    captureClosed: 'Den här inspelningen har redan stoppats.',
    captureActiveElsewhere: 'Du spelar redan in ett annat möte.',
    recordUnavailable: 'Inspelning är inte tillgänglig just nu.',
    stopFailed:
      'Inspelningen kunde inte stoppas. Kontrollera anslutningen eller logga in igen och försök sedan igen.',
    signOutRecordingTitle: 'En mötesinspelning pågår fortfarande',
    signOutRecordingBody:
      'Om du loggar ut stoppas den. Ljud som inte har laddats upp ännu stannar på den här enheten och laddas upp nästa gång du loggar in här.',
    signOutAnyway: 'Logga ut',
    paymentRequired:
      'Företagets användningskredit är slut. En administratör kan lägga till ett kort, försök sedan igen.',
  },
};
export default meetingsRecordCopy;
